<script lang="ts">
  /**
   * DocsPanel.svelte - Panneau de gestion des documents
   *
   * Affiche la liste des documents avec recherche, tri, filtrage
   * et actions (créer, modifier, supprimer).
   * Documents groupés par section: frontmatter, bodymatter, backmatter
   */

  import { tick } from 'svelte';
  import {
    docs,
    images,
    templates,
    selectedDoc,
    selectedProject,
    selectedPreset,
    presetMode,
    editingTemplate,
    sortedDocs,
    appActions,
    loadingState,
    errors,
    type SortColumn
  } from '../../stores/appStore';
  import { editorActions } from '../../stores/editorStore';
  import { i18n, translations } from '../../stores/i18nStore';
  import type { LanguageCode } from '../../stores/i18nStore';
  import { getDocumentFilename, generateFilenames } from '../../utils/fileNaming';
  import { countWords, countCharacters } from '../../utils/textStats';
  import { exportToEpub, exportDocument, type EpubImage, type EpubTemplate, type EpubLuaFilter, type ExportFormat } from '../../services/pandocService';
  import { DOCUMENT_TYPE_TO_EPUB_TYPE, applyEpubTypeToMarkdown } from '../../plugins/epubTypePlugin';
  import { db } from '../../services/indexedDBService';
  import { patchPlaceholders, parseProjectMetadata, resolveBibliographyPlaceholder } from '../../services/placeholderService';
  import JSZip from 'jszip';
  import DocumentImporter from '../import/DocumentImporter.svelte';
  import ProjectConfigPanel from '../config/ProjectConfigPanel.svelte';
  import type { Document, DocumentStatus, DocumentType, Image, Template, CreateImageInput, CreateTemplateInput, TemplateType, TemplateSection, ImageSection } from '../../types';

  // Tab type
  type TabId = 'text' | 'images' | 'templates';

  // Types
  interface ContextMenuPosition {
    x: number;
    y: number;
  }

  interface SectionConfig {
    id: string;
    label: string;
    icon: string;
    description: string;
  }

  type SectionId = 'undefined' | 'frontmatter' | 'bodymatter' | 'backmatter';

  interface CollapsedSections {
    undefined: boolean;
    frontmatter: boolean;
    bodymatter: boolean;
    backmatter: boolean;
  }

  interface _GroupedDocs {
    undefined: Document[];
    frontmatter: Document[];
    bodymatter: Document[];
    backmatter: Document[];
  }

  // État local
  let localSearchQuery: string = '';
  let statusFilter: string = 'all'; // all, todo, in-progress, done
  let showNewDocForm: boolean = false;
  let newDocTitle: string = '';
  let newDocType: DocumentType | '' = ''; // Type for new document
  let contextMenuDoc: Document | null = null;
  let contextMenuPosition: ContextMenuPosition = { x: 0, y: 0 };
  let contextMenuElement: HTMLDivElement | null = null;
  let showImporter: boolean = false;
  let isExporting: boolean = false;
  let showExportMenu: boolean = false;
  let collapsedSections: CollapsedSections = { undefined: false, frontmatter: false, bodymatter: false, backmatter: false };

  // Multi-selection state
  let selectedDocs: Set<string> = new Set();
  let lastSelectedDocId: string | null = null;
  let showTypeSubmenu: boolean = false;

  // Tab state
  let activeTab: TabId = 'text';

  // Detect article projects (single document, no text tab)
  $: isArticle = $selectedProject?.projectType === 'article';

  // For article projects: auto-select the single document and switch tab
  $: if (isArticle && $docs.length > 0 && !$selectedDoc) {
    appActions.selectDoc($docs[0]);
  }
  $: if (isArticle && activeTab === 'text') {
    activeTab = 'images';
  }

  // Import state
  let selectedImage: Image | null = null;
  let selectedTemplate: Template | null = null;

  // Template creation state
  let showNewTemplateForm: boolean = false;
  let newTemplateDocType: DocumentType | '' = '';

  // Config panel state
  let showConfigPanel: boolean = false;

  // Auto-show config panel when a preset is selected for editing
  $: if ($presetMode && $selectedPreset) {
    showConfigPanel = true;
  }

  // Preset template resolution: merge project + preset templates
  // Preset templates are shown alongside project templates when a project has a presetId
  let presetTplsForProject: Template[] = [];
  let projectPresetId: string = '';

  // Detect presetId from project's general.json template
  $: {
    if (!$presetMode && $selectedProject && $templates.length >= 0) {
      const generalTpl = $templates.find(t => t.filename === 'general.json');
      let newPresetId = '';
      if (generalTpl?.content) {
        try {
          const parsed = JSON.parse(generalTpl.content);
          newPresetId = parsed.presetId || '';
        } catch { /* ignore */ }
      }
      if (newPresetId !== projectPresetId) {
        projectPresetId = newPresetId;
        if (newPresetId) {
          appActions.loadPresetTemplates(newPresetId).then(tpls => {
            presetTplsForProject = tpls;
          });
        } else {
          presetTplsForProject = [];
        }
      }
    }
  }

  // Effective templates: project templates + preset templates (project overrides by filename)
  // Config templates (json/mdconfig) from preset are excluded since they were already copied to the project
  $: effectiveTemplates = (() => {
    if (!projectPresetId || presetTplsForProject.length === 0) return $templates;
    const projectFilenames = new Set($templates.map(t => t.filename));
    const configFilenames = new Set(['mapping.json', 'typography.json', 'pagelayout.json', 'grep.json', 'export.json', 'general.json']);
    const presetOnly = presetTplsForProject.filter(
      t => !projectFilenames.has(t.filename) && !configFilenames.has(t.filename)
    );
    return [...$templates, ...presetOnly];
  })();

  // Track which template IDs are from a preset (for visual indication)
  $: presetTemplateIds = new Set(presetTplsForProject.map(t => t.id));

  // Toast notification state
  let toastMessage: string = '';
  let toastVisible: boolean = false;

  // Configuration des sections
  const sections: Record<SectionId, SectionConfig> = {
    undefined: {
      id: 'undefined',
      label: 'Undefined type',
      icon: '❓',
      description: 'Choose a document type to classify'
    },
    frontmatter: {
      id: 'frontmatter',
      label: 'Frontmatter',
      icon: '📖',
      description: 'Preface, introduction, foreword...'
    },
    bodymatter: {
      id: 'bodymatter',
      label: 'Bodymatter',
      icon: '📝',
      description: 'Main chapters'
    },
    backmatter: {
      id: 'backmatter',
      label: 'Backmatter',
      icon: '📚',
      description: 'Appendices, bibliography, index...'
    }
  };

  // L'ordre d'affichage - 'undefined' n'est affiché que s'il y a des documents sans type
  const sectionOrder: SectionId[] = ['undefined', 'frontmatter', 'bodymatter', 'backmatter'];

  // ============================================================================
  // TEMPLATE SECTIONS CONFIGURATION
  // ============================================================================

  interface TemplateSectionConfig {
    id: TemplateSection;
    label: string;
    icon: string;
    description: string;
  }

  interface CollapsedTemplateSections {
    undefined: boolean;
    epub3: boolean;
    epub2: boolean;
    fonts: boolean;
    preparo: boolean;
    luafilters: boolean;
  }

  // Configuration des sections de templates
  const templateSections: Record<TemplateSection, TemplateSectionConfig> = {
    undefined: {
      id: 'undefined',
      label: 'Unassigned',
      icon: '❓',
      description: 'Drag CSS files to EPUB 3 or EPUB 2 sections'
    },
    epub3: {
      id: 'epub3',
      label: 'EPUB 3',
      icon: '📘',
      description: 'CSS files for EPUB 3 format'
    },
    epub2: {
      id: 'epub2',
      label: 'EPUB 2',
      icon: '📗',
      description: 'CSS files for EPUB 2 format'
    },
    fonts: {
      id: 'fonts',
      label: 'Fonts',
      icon: '🔤',
      description: 'Font files (TTF, OTF, WOFF, WOFF2)'
    },
    preparo: {
      id: 'preparo',
      label: 'Preparo',
      icon: '📝',
      description: 'Preparo document templates (Markdown)'
    },
    luafilters: {
      id: 'luafilters',
      label: 'Lua Filters',
      icon: '🌙',
      description: 'Pandoc Lua filters for custom processing'
    },
    csl: {
      id: 'csl',
      label: 'Bibliography Style (CSL)',
      icon: '📚',
      description: 'Citation Style Language files for bibliography formatting'
    },
    indesign: {
      id: 'indesign',
      label: 'InDesign',
      icon: '📐',
      description: 'InDesign template files (.indd)'
    }
  };

  const templateSectionOrder: TemplateSection[] = ['preparo', 'undefined', 'epub3', 'epub2', 'fonts', 'luafilters'];

  // ePub group: subsections in alphabetical order (FR): Filtre Lua, Font, Style
  // "Style" sub-group contains the CSS sections: undefined, epub3, epub2
  const epubSubsectionOrder: TemplateSection[] = ['luafilters', 'fonts'];
  const styleSectionOrder: TemplateSection[] = ['undefined', 'epub3', 'epub2'];

  let preparoGroupCollapsed = true;
  let epubGroupCollapsed = true;
  let styleGroupCollapsed = true;
  let cslGroupCollapsed = true;
  let indesignGroupCollapsed = true;

  let collapsedTemplateSections: CollapsedTemplateSections = {
    undefined: true,
    epub3: true,
    epub2: true,
    fonts: true,
    preparo: true,
    luafilters: true,
    csl: true
  };

  // Templates groupés par section (uses effectiveTemplates to include preset templates)
  $: groupedTemplates = {
    undefined: effectiveTemplates.filter(t => t.templateSection === 'undefined'),
    epub3: effectiveTemplates.filter(t => t.templateSection === 'epub3'),
    epub2: effectiveTemplates.filter(t => t.templateSection === 'epub2'),
    fonts: effectiveTemplates.filter(t => t.templateSection === 'fonts'),
    preparo: effectiveTemplates.filter(t => t.templateSection === 'preparo'),
    luafilters: effectiveTemplates.filter(t => t.templateSection === 'luafilters'),
    csl: effectiveTemplates.filter(t => t.templateSection === 'csl'),
    indesign: effectiveTemplates.filter(t => t.templateSection === 'indesign')
  };

  // Computed counts for ePub group and Style sub-group
  $: styleTemplateCount = (groupedTemplates.undefined?.length || 0) + (groupedTemplates.epub3?.length || 0) + (groupedTemplates.epub2?.length || 0);
  $: epubTemplateCount = effectiveTemplates.filter(t => t.templateSection !== 'csl' && t.templateSection !== 'preparo' && t.templateSection !== 'indesign' && t.templateSection !== 'config').length;
  $: preparoTemplateCount = groupedTemplates.preparo?.length || 0;
  $: indesignTemplateCount = groupedTemplates.indesign?.length || 0;

  // Count top-level sections (currently only "ePub" exists)
  $: templateTopSectionCount = $templates.length > 0 ? 1 : 0;

  // Toggle template section collapse
  function toggleTemplateSection(sectionId: TemplateSection): void {
    collapsedTemplateSections[sectionId] = !collapsedTemplateSections[sectionId];
  }

  // ============================================================================
  // TEMPLATE DRAG & DROP (for moving between sections)
  // ============================================================================

  let draggedTemplate: Template | null = null;
  let dragOverTemplateSection: TemplateSection | null = null;

  function handleTemplateDragStart(event: DragEvent, template: Template): void {
    // Only allow dragging CSS files (not fonts)
    if (template.templateType !== 'css') {
      event.preventDefault();
      return;
    }
    draggedTemplate = template;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', template.id);
    }
    (event.target as HTMLElement).classList.add('dragging');
  }

  function handleTemplateDragEnd(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('dragging');
    draggedTemplate = null;
    dragOverTemplateSection = null;
  }

  function handleTemplateSectionDragOver(event: DragEvent, sectionId: TemplateSection): void {
    event.preventDefault();

    // Only allow dropping in CSS sections (not fonts or luafilters)
    if (sectionId === 'fonts' || sectionId === 'luafilters' || !draggedTemplate || draggedTemplate.templateType !== 'css') {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      return;
    }

    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    dragOverTemplateSection = sectionId;
  }

  function handleTemplateSectionDragLeave(event: DragEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node)) {
      dragOverTemplateSection = null;
    }
  }

  async function handleTemplateSectionDrop(event: DragEvent, targetSection: TemplateSection): Promise<void> {
    event.preventDefault();

    if (!draggedTemplate || targetSection === 'fonts' || targetSection === 'luafilters' || draggedTemplate.templateType !== 'css') {
      draggedTemplate = null;
      dragOverTemplateSection = null;
      return;
    }

    // Don't update if already in this section
    if (draggedTemplate.templateSection === targetSection) {
      draggedTemplate = null;
      dragOverTemplateSection = null;
      return;
    }

    try {
      await appActions.updateTemplate(draggedTemplate.id, { templateSection: targetSection });
      console.log(`✅ Moved template "${draggedTemplate.filename}" to section "${targetSection}"`);
    } catch (error) {
      console.error('Failed to move template:', error);
    }

    draggedTemplate = null;
    dragOverTemplateSection = null;
  }

  // ============================================================================
  // IMAGE SECTIONS CONFIGURATION
  // ============================================================================

  interface ImageSectionConfig {
    id: ImageSection;
    label: string;
    icon: string;
    description: string;
  }

  interface CollapsedImageSections {
    cover: boolean;
    images: boolean;
  }

  // Configuration des sections d'images
  const imageSections: Record<ImageSection, ImageSectionConfig> = {
    cover: {
      id: 'cover',
      label: 'Cover',
      icon: '📕',
      description: 'Book cover image'
    },
    images: {
      id: 'images',
      label: 'Images',
      icon: '🖼️',
      description: 'All other images'
    }
  };

  const imageSectionOrder: ImageSection[] = ['cover', 'images'];

  let collapsedImageSections: CollapsedImageSections = {
    cover: false,
    images: false
  };

  // Images groupées par section
  $: groupedImages = {
    cover: $images.filter(img => img.imageSection === 'cover'),
    images: $images.filter(img => img.imageSection === 'images')
  };

  // Toggle image section collapse
  function toggleImageSection(sectionId: ImageSection): void {
    collapsedImageSections[sectionId] = !collapsedImageSections[sectionId];
  }

  // ============================================================================
  // IMAGE DRAG & DROP (for moving between sections)
  // ============================================================================

  let draggedImage: Image | null = null;
  let dragOverImageSection: ImageSection | null = null;

  function handleImageSectionDragStart(event: DragEvent, image: Image): void {
    draggedImage = image;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', image.id);
    }
    (event.target as HTMLElement).classList.add('dragging');
  }

  function handleImageSectionDragEnd(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('dragging');
    draggedImage = null;
    dragOverImageSection = null;
  }

  function handleImageSectionDragOver(event: DragEvent, sectionId: ImageSection): void {
    event.preventDefault();

    if (!draggedImage) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      return;
    }

    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    dragOverImageSection = sectionId;
  }

  function handleImageSectionDragLeave(event: DragEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node)) {
      dragOverImageSection = null;
    }
  }

  async function handleImageSectionDrop(event: DragEvent, targetSection: ImageSection): Promise<void> {
    event.preventDefault();

    if (!draggedImage) {
      draggedImage = null;
      dragOverImageSection = null;
      return;
    }

    // Don't update if already in this section
    if (draggedImage.imageSection === targetSection) {
      draggedImage = null;
      dragOverImageSection = null;
      return;
    }

    try {
      await db.images.update(draggedImage.id, { imageSection: targetSection });
      // Reload images to reflect the change
      if ($selectedProject) {
        await appActions.loadImages($selectedProject.id);
      }
      console.log(`✅ Moved image "${draggedImage.filename}" to section "${targetSection}"`);
    } catch (error) {
      console.error('Failed to move image:', error);
    }

    draggedImage = null;
    dragOverImageSection = null;
  }

  // Documents filtrés par statut
  $: filteredByStatus = statusFilter === 'all'
    ? $sortedDocs
    : $sortedDocs.filter(doc => doc.status === statusFilter);

  // Fonction pour trier par order (les documents sans order vont à la fin)
  function sortByOrder(docs: Document[]): Document[] {
    return [...docs].sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      return orderA - orderB;
    });
  }

  // Documents groupés par section et triés par order
  // Les documents sans type défini vont dans "undefined"
  // Les autres vont dans leur section selon documentSection
  $: groupedDocs = {
    undefined: sortByOrder(filteredByStatus.filter(doc => !doc.documentType)),
    frontmatter: sortByOrder(filteredByStatus.filter(doc => doc.documentType && doc.documentSection === 'frontmatter')),
    bodymatter: sortByOrder(filteredByStatus.filter(doc => doc.documentType && (!doc.documentSection || doc.documentSection === 'bodymatter'))),
    backmatter: sortByOrder(filteredByStatus.filter(doc => doc.documentType && doc.documentSection === 'backmatter'))
  };

  // Synchroniser la recherche avec le store
  $: appActions.setSearchQuery(localSearchQuery);

  // Icônes pour les statuts
  const statusIcons: Record<string, string> = {
    'todo': '⭕',
    'in-progress': '🔄',
    'done': '✅',
    'archived': '📦'
  };

  // Couleurs pour les statuts
  const statusColors: Record<string, string> = {
    'todo': '#6b7280',
    'in-progress': '#f59e0b',
    'done': '#10b981',
    'archived': '#9ca3af'
  };

  // Toggle section collapse
  function toggleSection(sectionId: SectionId): void {
    collapsedSections[sectionId] = !collapsedSections[sectionId];
  }

  // Structure des types de documents (same as NotesPanel)
  interface DocumentTypeOption {
    value: string;
    label: string;
  }

  interface DocumentTypeCategory {
    label: string;
    types: DocumentTypeOption[];
  }

  type DocumentTypesConfig = Record<string, DocumentTypeCategory>;

  const DOCUMENT_TYPES: DocumentTypesConfig = {
    bodymatter: {
      label: 'Bodymatter',
      types: [
        { value: 'chapter', label: 'Chapter' },
        { value: 'part', label: 'Part' }
      ]
    },
    frontmatter: {
      label: 'Frontmatter',
      types: [
        { value: 'titlepage', label: 'Title Page' },
        { value: 'halftitlepage', label: 'Half Title Page' },
        { value: 'copyright-page', label: 'Copyright Page' },
        { value: 'dedication', label: 'Dedication' },
        { value: 'prologue', label: 'Prologue' },
        { value: 'abstract', label: 'Abstract' },
        { value: 'acknowledgments', label: 'Acknowledgments' },
        { value: 'credits', label: 'Credits' },
        { value: 'other-credits', label: 'Other Credits' },
        { value: 'keywords', label: 'Keywords' },
        { value: 'imprint', label: 'Imprint' },
        { value: 'contributors', label: 'Contributors' },
        { value: 'errata', label: 'Errata' },
        { value: 'revision-history', label: 'Revision History' },
        { value: 'seriespage', label: 'Series Page' },
        { value: 'foreword', label: 'Foreword' },
        { value: 'preface', label: 'Preface' },
        { value: 'frontispiece', label: 'Frontispiece' },
        { value: 'introduction', label: 'Introduction' }
      ]
    },
    backmatter: {
      label: 'Backmatter',
      types: [
        { value: 'appendix', label: 'Appendix' },
        { value: 'colophon', label: 'Colophon' },
        { value: 'bibliography', label: 'Bibliography' },
        { value: 'index', label: 'Index' },
        { value: 'conclusion', label: 'Conclusion' },
        { value: 'annexe', label: 'Annexe' },
        { value: 'postface', label: 'Postface' }
      ]
    }
  };

  // Obtenir la section (category) d'un type de document
  function getSectionFromType(documentType: string | null | undefined): 'frontmatter' | 'bodymatter' | 'backmatter' | null {
    if (!documentType) return null;

    for (const [category, data] of Object.entries(DOCUMENT_TYPES)) {
      if (data.types.some(t => t.value === documentType)) {
        return category as 'frontmatter' | 'bodymatter' | 'backmatter';
      }
    }
    return null;
  }

  // Sélectionner un document
  function handleSelectDoc(doc: Document, event?: MouseEvent): void {
    const isCtrlOrCmd = event?.ctrlKey || event?.metaKey;
    const isShift = event?.shiftKey;

    if (isCtrlOrCmd) {
      // Ctrl/Cmd+click: toggle selection
      selectedDocs = new Set(selectedDocs);
      if (selectedDocs.has(doc.id)) {
        selectedDocs.delete(doc.id);
      } else {
        selectedDocs.add(doc.id);
      }
      lastSelectedDocId = doc.id;
    } else if (isShift && lastSelectedDocId) {
      // Shift+click: select range
      const allDocs = $sortedDocs;
      const lastIndex = allDocs.findIndex(d => d.id === lastSelectedDocId);
      const currentIndex = allDocs.findIndex(d => d.id === doc.id);

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        selectedDocs = new Set(selectedDocs);
        for (let i = start; i <= end; i++) {
          selectedDocs.add(allDocs[i].id);
        }
      }
    } else {
      // Normal click: select only this document
      selectedDocs = new Set([doc.id]);
      lastSelectedDocId = doc.id;
      // Clear template editing mode when selecting a document
      editingTemplate.set(null);
      selectedTemplate = null;
      appActions.selectDoc(doc);
    }

    contextMenuDoc = null;
    showTypeSubmenu = false;
  }

  // Check if a document is multi-selected
  function isDocSelected(docId: string): boolean {
    return selectedDocs.has(docId);
  }

  // Get count of selected documents
  $: selectedDocsCount = selectedDocs.size;

  // Créer un nouveau document
  async function handleCreateDoc(): Promise<void> {
    if (!newDocTitle.trim()) return;

    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProject'));
      return;
    }

    try {
      // Determine section from type
      const docType = newDocType || null;
      const docSection = getSectionFromType(docType);

      // Check for matching Preparo template to use as initial content
      let initialContent: string | undefined;
      if (docType) {
        // 1. Match by documentType field
        let matchingTemplate = $templates.find(
          t => t.templateType === 'preparo' && t.documentType === docType
        );
        // 2. Fallback: match by epub:type in template content
        if (!matchingTemplate) {
          matchingTemplate = $templates.find(t => {
            if (t.templateType !== 'preparo' || !t.content) return false;
            const detected = detectDocTypeFromMarkdown(t.content);
            return detected.documentType === docType;
          });
        }
        if (matchingTemplate) {
          initialContent = matchingTemplate.content;
          console.log(`Using template "${matchingTemplate.filename}" for new ${docType} document`);
        }
      }

      const newDoc = await appActions.createDoc({
        projectId: $selectedProject.id,
        title: newDocTitle.trim(),
        status: 'todo',
        wordCount: 0,
        charCount: 0,
        content: initialContent,
        documentType: docType as DocumentType | null,
        documentSection: docSection
      });

      // Sélectionner le nouveau document
      appActions.selectDoc(newDoc);

      // Réinitialiser le formulaire
      newDocTitle = '';
      newDocType = '';
      showNewDocForm = false;
    } catch (error) {
      console.error('Failed to create doc:', error);
    }
  }

  // Annuler la création
  function handleCancelCreate(): void {
    newDocTitle = '';
    newDocType = '';
    showNewDocForm = false;
  }

  // Créer un nouveau template Preparo
  async function handleCreateMarkdownTemplate(): Promise<void> {
    if (!newTemplateDocType) return;

    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProject'));
      return;
    }

    try {
      // Nom du fichier basé sur le type de document
      const filename = `${newTemplateDocType}.md`;

      await appActions.addTemplate({
        projectId: $selectedProject.id,
        filename,
        templateType: 'preparo',
        templateSection: 'preparo',
        mimeType: 'text/markdown',
        content: `# ${newTemplateDocType}\n\n<!-- Template content here -->\n`,
        size: 0,
        documentType: newTemplateDocType as DocumentType
      });

      // Réinitialiser le formulaire
      newTemplateDocType = '';
      showNewTemplateForm = false;
    } catch (error) {
      console.error('Failed to create Preparo template:', error);
    }
  }

  // Annuler la création de template
  function handleCancelTemplateCreate(): void {
    newTemplateDocType = '';
    showNewTemplateForm = false;
  }

  // Type pour l'event de conversion de document
  interface ConvertedFile {
    markdown: string;
    filename: string;
    metadata: {
      title?: string;
      wordCount?: number;
    };
  }

  interface ConvertedEventDetail {
    files: ConvertedFile[];
    count: number;
  }

  // Build reverse mapping: epub:type value → document type
  const EPUB_TYPE_TO_DOCUMENT_TYPE: Record<string, string> = {};
  for (const [docType, epubType] of Object.entries(DOCUMENT_TYPE_TO_EPUB_TYPE)) {
    // If multiple docTypes map to the same epubType, prefer the first one
    if (!EPUB_TYPE_TO_DOCUMENT_TYPE[epubType]) {
      EPUB_TYPE_TO_DOCUMENT_TYPE[epubType] = docType;
    }
  }

  /**
   * Detect document type from epub:type in the first H1 heading.
   * Parses: # Title {epub:type=titlepage ...}
   */
  function detectDocTypeFromMarkdown(markdown: string): { documentType: DocumentType | null, documentSection: 'frontmatter' | 'bodymatter' | 'backmatter' | null } {
    const h1Match = markdown.match(/^#\s+.+\{([^}]+)\}\s*$/m);
    if (!h1Match) return { documentType: null, documentSection: null };

    const attrString = h1Match[1];
    const epubTypeMatch = attrString.match(/epub:type=(\S+)/);
    if (!epubTypeMatch) return { documentType: null, documentSection: null };

    const epubType = epubTypeMatch[1].replace(/^["']|["']$/g, '');
    const docType = EPUB_TYPE_TO_DOCUMENT_TYPE[epubType] || null;

    if (!docType) return { documentType: null, documentSection: null };

    const section = getSectionFromType(docType);
    return { documentType: docType as DocumentType, documentSection: section };
  }

  // Gérer l'import de documents Word convertis (un ou plusieurs)
  async function handleDocumentConverted(event: CustomEvent<ConvertedEventDetail>): Promise<void> {
    const { files, count } = event.detail;

    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProjectImport'));
      return;
    }

    if (!files || files.length === 0) {
      return;
    }

    try {
      console.log(`📥 IMPORTING ${count} document(s)...`);

      let lastDoc: Document | null = null;
      let successCount = 0;

      for (const file of files) {
        const { markdown, filename, metadata } = file;
        const title = metadata.title || filename.replace('.md', '');

        console.log(`  📄 Importing: "${title}" (${markdown.length} chars)`);

        try {
          // Auto-detect document type from epub:type in H1 heading
          const detected = detectDocTypeFromMarkdown(markdown);

          // Use section info from ZIP manifest if available
          const manifestSection = (metadata as any)?._section as 'frontmatter' | 'bodymatter' | 'backmatter' | undefined;
          if (manifestSection && !detected.documentSection) {
            detected.documentSection = manifestSection;
          }

          // Generate YAML frontmatter with shorttitle if document type was detected
          let contentWithFrontmatter = markdown;
          if (detected.documentType) {
            const meta = parseProjectMetadata($selectedProject?.metadata);
            const langCode = meta.lang?.slice(0, 2)?.toLowerCase();
            const lang: LanguageCode = (langCode === 'en' || langCode === 'fr') ? langCode as LanguageCode : 'fr';
            const typeLabel = translations[lang]?.[`docType.${detected.documentType}`]
              || translations['en']?.[`docType.${detected.documentType}`]
              || detected.documentType;

            // Check if content already has frontmatter
            const fmMatch = markdown.match(/^---\n([\s\S]*?\n)(?:---|\.\.\.)\n?/);
            if (fmMatch) {
              // Add shorttitle to existing frontmatter if not present
              const fm = fmMatch[0];
              if (!/^shorttitle:\s/m.test(fm)) {
                const newFm = fm.replace(/^(---\n?)$/m, `shorttitle: ${typeLabel}\n$1`);
                contentWithFrontmatter = newFm + markdown.slice(fm.length);
              }
            } else {
              // Create frontmatter from scratch
              contentWithFrontmatter = `---\nshorttitle: ${typeLabel}\n---\n\n${markdown}`;
            }
          }

          const newDoc = await appActions.createDoc({
            projectId: $selectedProject.id,
            title,
            filename,
            status: 'todo',
            content: contentWithFrontmatter,
            wordCount: metadata.wordCount || countWords(contentWithFrontmatter),
            charCount: countCharacters(contentWithFrontmatter),
            documentType: detected.documentType,
            documentSection: detected.documentSection
          });

          lastDoc = newDoc;
          successCount++;
        } catch (docError) {
          console.error(`  ❌ Failed to import "${title}":`, docError);
        }
      }

      // Sélectionner le dernier document importé
      if (lastDoc) {
        console.log(`🎯 SELECTING - Doc ID: ${lastDoc.id}, Title: "${lastDoc.title}"`);
        appActions.selectDoc(lastDoc);
      }

      // Fermer l'importeur
      showImporter = false;

      console.log(`✅ ${successCount}/${count} document(s) imported successfully`);

      if (successCount < count) {
        alert($i18n('docs.importPartial', { success: successCount, total: count }));
      }
    } catch (error) {
      console.error('❌ Import failed:', error);
      alert($i18n('docs.importError'));
    }
  }

  // Handle images imported from DocumentImporter
  interface ImagesImportedEventDetail {
    images: CreateImageInput[];
    count: number;
  }

  async function handleImagesImported(event: CustomEvent<ImagesImportedEventDetail>): Promise<void> {
    const { images, count } = event.detail;

    if (!$selectedProject) return;

    console.log(`📥 Importing ${count} image(s)...`);

    let successCount = 0;
    for (const imageData of images) {
      try {
        await appActions.addImage({
          ...imageData,
          projectId: $selectedProject.id
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to import image ${imageData.filename}:`, error);
      }
    }

    console.log(`✅ ${successCount}/${count} image(s) imported`);

    // Switch to images tab
    activeTab = 'images';

    // Close importer if no other files pending
    showImporter = false;
  }

  // Handle metadata imported from ZIP
  interface MetadataImportedEventDetail {
    metadata: string;
    title?: string;
    author?: string;
  }

  async function handleMetadataImported(event: CustomEvent<MetadataImportedEventDetail>): Promise<void> {
    const { metadata, title, author } = event.detail;

    if (!$selectedProject) return;

    console.log(`📋 Importing metadata from ZIP...`);

    try {
      const updates: any = { metadata };
      if (title) updates.title = title;
      if (author) updates.author = author;
      await appActions.updateProject($selectedProject.id, updates);
      console.log('✅ Project metadata updated from ZIP');
    } catch (error) {
      console.error('❌ Failed to update metadata from ZIP:', error);
    }
  }

  // Handle templates imported from DocumentImporter
  interface TemplatesImportedEventDetail {
    templates: CreateTemplateInput[];
    count: number;
  }

  async function handleTemplatesImported(event: CustomEvent<TemplatesImportedEventDetail>): Promise<void> {
    const { templates, count } = event.detail;

    console.log(`📥 handleTemplatesImported called with ${count} template(s):`, templates.map(t => ({ filename: t.filename, type: t.templateType, section: t.templateSection })));

    if (!$selectedProject) {
      console.error('❌ No project selected, cannot import templates');
      return;
    }

    console.log(`📥 Importing ${count} template(s) to project ${$selectedProject.id}...`);

    let successCount = 0;
    for (const templateData of templates) {
      try {
        console.log(`  → Adding template: ${templateData.filename} (type: ${templateData.templateType}, section: ${templateData.templateSection})`);
        const result = await appActions.addTemplate({
          ...templateData,
          projectId: $selectedProject.id
        });
        console.log(`  ✅ Template added:`, result);
        successCount++;
      } catch (error) {
        console.error(`  ❌ Failed to import template ${templateData.filename}:`, error);
      }
    }

    console.log(`✅ ${successCount}/${count} template(s) imported`);

    // Switch to templates tab
    activeTab = 'templates';

    // Close importer if no other files pending
    showImporter = false;
  }

  // Exporter le projet en EPUB
  async function handleExportEpub(epubVersion: 'epub2' | 'epub3' = 'epub3'): Promise<void> {
    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProject'));
      return;
    }

    // Récupérer tous les documents avec un type (frontmatter, bodymatter, backmatter)
    const docsWithType = $docs.filter(doc => doc.documentType);

    if (docsWithType.length === 0) {
      alert($i18n('docs.noDocsToExport'));
      return;
    }

    isExporting = true;

    try {
      // Créer les chapitres avec leur nom de fichier
      // Triés par ordre alphabétique du nom de fichier
      const docsWithFilenames = docsWithType
        .map(doc => ({
          filename: getDocumentFilename(doc, $docs),
          doc
        }))
        .filter(ch => ch.filename) // S'assurer que le filename existe
        .sort((a, b) => a.filename.localeCompare(b.filename));

      if (docsWithFilenames.length === 0) {
        alert($i18n('docs.noDocsToExport'));
        return;
      }

      // Charger le contenu de chaque document depuis le stockage
      // et résoudre les placeholders {{TITLE}}, {{AUTHOR}}, {{bibliography}}, etc.
      console.log(`📚 Loading content for ${docsWithFilenames.length} documents...`);
      const yamlMeta = $selectedProject?.metadata;

      // Get CSL style for bibliography resolution
      const cslTemplate = $templates.find(
        (t) => t.templateSection === 'csl' || t.templateType === 'csl' || t.filename?.toLowerCase().includes('.csl')
      );

      const chapters = await Promise.all(
        docsWithFilenames.map(async ({ filename, doc }) => {
          const rawContent = await db.contents.get(doc.id) || '';
          // Resolve {{TITLE}}, {{AUTHOR}}, etc.
          let content = patchPlaceholders(rawContent, yamlMeta);
          // Resolve {{bibliography}} placeholder
          content = await resolveBibliographyPlaceholder(content, $selectedProject!.id, cslTemplate?.content);
          return { filename, content };
        })
      );

      // Filtrer les chapitres sans contenu
      const validChapters = chapters.filter(ch => ch.content && ch.content.trim().length > 0);

      if (validChapters.length === 0) {
        alert($i18n('docs.noContentToExport'));
        return;
      }

      console.log(`📚 Exporting ${validChapters.length} chapters to EPUB...`);
      console.log('Chapters order:', validChapters.map(c => c.filename));

      // Préparer les images pour l'export
      // Convertir les data URLs en format EpubImage (sans le préfixe data:...)
      const epubImages: EpubImage[] = $images.map(img => {
        // Extraire les données base64 du data URL
        const base64Match = img.dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!base64Match) {
          console.warn(`Invalid data URL for image: ${img.filename}`);
          return null;
        }
        return {
          filename: img.filename,
          mimeType: img.mimeType,
          data: base64Match[2] // Données base64 sans préfixe
        };
      }).filter((img): img is EpubImage => img !== null);

      console.log(`📷 Including ${epubImages.length} images in EPUB`);

      // Préparer les templates pour l'export (exclure les filtres Lua)
      // Pour les CSS : inclure uniquement ceux de la section correspondant à la version
      // choisie (epub3 ou epub2) + ceux en section "undefined" (non assignés).
      // Les fonts et autres templates sont toujours inclus.
      const excludeSection = epubVersion === 'epub3' ? 'epub2' : 'epub3';
      const epubTemplates: EpubTemplate[] = $templates
        .filter(tpl => tpl.templateType !== 'lua')
        .filter(tpl => {
          if (tpl.templateType === 'css' && tpl.templateSection === excludeSection) {
            return false;
          }
          return true;
        })
        .map(tpl => ({
          filename: tpl.filename,
          templateType: tpl.templateType as 'css' | 'font' | 'other',
          mimeType: tpl.mimeType,
          content: tpl.content
        }));

      console.log(`📎 Including ${epubTemplates.length} templates in EPUB`);

      // Préparer les filtres Lua pour l'export
      const luaFilters: EpubLuaFilter[] = $templates
        .filter(tpl => tpl.templateType === 'lua')
        .map(tpl => ({
          filename: tpl.filename,
          content: tpl.content
        }));

      if (luaFilters.length > 0) {
        console.log(`🌙 Including ${luaFilters.length} Lua filter(s): ${luaFilters.map(f => f.filename).join(', ')}`);
      }

      // Détecter si un document titlepage existe dans le frontmatter
      // Si oui, désactiver la page de titre Pandoc automatique
      const hasTitlepage = docsWithType.some(doc => doc.documentType === 'titlepage');

      if (hasTitlepage) {
        console.log('📄 Custom titlepage detected — disabling Pandoc auto title page');
      }

      // Extract lang from project metadata for post-processing (cover SVG title, dc:language)
      const parsedMeta = parseProjectMetadata($selectedProject.metadata);
      const lang = parsedMeta.lang || 'en';

      // Default TOC title based on project language (not UI language)
      const defaultTocTitle = lang.startsWith('fr') ? 'Table des matières' : 'Table of Contents';

      // Extract EPUB export settings from project settings JSON (not from YAML)
      let navReorder = true;
      let toc = true;
      let tocDepth = 2;
      let tocTitle = '';
      if ($selectedProject.settings) {
        try {
          const settings = JSON.parse($selectedProject.settings);
          if (typeof settings.navReorder === 'boolean') navReorder = settings.navReorder;
          if (typeof settings.toc === 'boolean') toc = settings.toc;
          if (typeof settings.tocDepth === 'number') tocDepth = settings.tocDepth;
          if (typeof settings.tocTitle === 'string') tocTitle = settings.tocTitle;
        } catch { /* use defaults */ }
      }

      await exportToEpub(
        validChapters,
        {
          title: $selectedProject.title,
          author: $selectedProject.author || '',
          yaml: $selectedProject.metadata || undefined
        },
        epubImages,
        epubTemplates,
        luaFilters,
        {
          epubTitlePage: hasTitlepage ? false : undefined,
          epubVersion,
          lang,
          navReorder,
          toc,
          tocDepth,
          tocTitle: tocTitle || defaultTocTitle,
        }
      );

      console.log('✅ EPUB export successful');
    } catch (error) {
      console.error('❌ EPUB export failed:', error);
      alert($i18n('docs.exportError', { message: (error as Error).message }));
    } finally {
      isExporting = false;
    }
  }

  // Exporter le projet dans un format spécifique (DOCX, ODT, PDF, HTML)
  async function handleExportFormat(format: ExportFormat): Promise<void> {
    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProject'));
      return;
    }

    // Récupérer tous les documents avec un type (frontmatter, bodymatter, backmatter)
    const docsWithType = $docs.filter(doc => doc.documentType);

    if (docsWithType.length === 0) {
      alert($i18n('docs.noDocsToExport'));
      return;
    }

    isExporting = true;

    try {
      // Trier les documents par nom de fichier
      const docsWithFilenames = docsWithType
        .map(doc => ({
          filename: getDocumentFilename(doc, $docs),
          doc
        }))
        .filter(ch => ch.filename)
        .sort((a, b) => a.filename.localeCompare(b.filename));

      if (docsWithFilenames.length === 0) {
        alert($i18n('docs.noDocsToExport'));
        return;
      }

      // Charger le contenu et résoudre les placeholders
      console.log(`📄 Loading content for ${docsWithFilenames.length} documents...`);
      const yamlMeta = $selectedProject?.metadata;

      // Get CSL style for bibliography resolution
      const cslTemplate = $templates.find(
        (t) => t.templateSection === 'csl' || t.templateType === 'csl' || t.filename?.toLowerCase().includes('.csl')
      );

      const chapters = await Promise.all(
        docsWithFilenames.map(async ({ filename, doc }) => {
          const rawContent = await db.contents.get(doc.id) || '';
          let content = patchPlaceholders(rawContent, yamlMeta);
          content = await resolveBibliographyPlaceholder(content, $selectedProject!.id, cslTemplate?.content);
          return { filename, content };
        })
      );

      // Filtrer les chapitres sans contenu
      const validChapters = chapters.filter(ch => ch.content && ch.content.trim().length > 0);

      if (validChapters.length === 0) {
        alert($i18n('docs.noContentToExport'));
        return;
      }

      console.log(`📤 Exporting ${validChapters.length} chapters to ${format.toUpperCase()} with --file-scope...`);

      // Utiliser le titre du projet comme nom de fichier
      const filename = $selectedProject.title.replace(/[^a-zA-Z0-9àâäéèêëïîôùûüç\s-]/gi, '').trim() || 'document';

      // Export with chapters array (uses --file-scope for isolated footnotes per chapter)
      await exportDocument('', filename, format, { chapters: validChapters });

      console.log(`✅ Export ${format.toUpperCase()} completed`);
    } catch (error) {
      console.error(`Export ${format} error:`, error);
      alert($i18n('docs.exportError', { message: (error as Error).message }));
    } finally {
      isExporting = false;
    }
  }



  // Exporter le projet en ZIP (structure flat, identique à exemple-livre/)
  // Structure cible :
  // projet.zip/
  //   manifest.yaml
  //   metadata.yaml              <- format Pandoc complet avec ---
  //   texts/*.md
  //   assets/*
  //   config/*.json
  //   template/
  //     epub/
  //       fonts/*.ttf
  //       lua/*.lua
  //       style/*.css
  //     csl/*.csl
  //     indesign/*.indd
  //     preparo/*.md
  //   build/
  async function handleExportZip(): Promise<void> {
    if (!$selectedProject) {
      alert($i18n('docs.pleaseSelectProject'));
      return;
    }

    isExporting = true;

    try {
      const zip = new JSZip();

      // Créer la structure de dossiers (flat — pas de dossier manuscript/)
      const textFolder = zip.folder('texts');
      const assetsFolder = zip.folder('assets');
      const configFolder = zip.folder('config');
      const templateFolder = zip.folder('template');
      const templateEpubFolder = templateFolder?.folder('epub');
      const templateEpubFontsFolder = templateEpubFolder?.folder('fonts');
      const templateEpubStyleFolder = templateEpubFolder?.folder('style');
      const templateEpubLuaFolder = templateEpubFolder?.folder('lua');
      const templatePreparoFolder = templateFolder?.folder('preparo');
      const templateCslFolder = templateFolder?.folder('csl');
      const templateIndesignFolder = templateFolder?.folder('indesign');
      const buildFolder = zip.folder('build');

      if (!textFolder || !assetsFolder || !configFolder) {
        throw new Error('Failed to create ZIP folders');
      }

      // === 1. Ajouter les documents dans texts/ ===
      const zipYamlMeta = $selectedProject?.metadata;

      // Use effectiveTemplates (project + preset templates) for autonomous export
      const allTemplates = effectiveTemplates;

      // Get CSL style for bibliography resolution
      const cslTemplate = allTemplates.find(
        (t: any) => t.templateSection === 'csl' || t.templateType === 'csl' || t.filename?.toLowerCase().includes('.csl')
      );

      for (const doc of $docs) {
        const filename = getDocumentFilename(doc, $docs) || `${doc.id}.md`;
        const rawContent = await db.contents.get(doc.id) || '';
        let content = patchPlaceholders(rawContent, zipYamlMeta);
        content = await resolveBibliographyPlaceholder(content, $selectedProject.id, cslTemplate?.content);
        textFolder.file(filename, content);
      }

      // === 2. Ajouter les images dans assets/ ===
      for (const img of $images) {
        const base64Match = img.dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (base64Match) {
          const base64Data = base64Match[2];
          assetsFolder.file(img.filename, base64Data, { base64: true });
        }
      }

      // === 3. Créer metadata.yaml (format Pandoc complet avec ---) ===
      let metadataYaml = '---\n';
      if ($selectedProject.metadata) {
        // Le metadata du projet est déjà en format Pandoc YAML complet
        let meta = $selectedProject.metadata.trim();
        // Retirer les --- existants si présents (éviter la duplication)
        if (meta.startsWith('---')) {
          meta = meta.replace(/^---\s*\n?/, '');
        }
        if (meta.endsWith('---')) {
          meta = meta.replace(/\n?---\s*$/, '');
        }
        metadataYaml += meta;
        if (!metadataYaml.endsWith('\n')) {
          metadataYaml += '\n';
        }
      } else {
        // Fallback : métadonnées basiques si pas de metadata Pandoc
        if ($selectedProject.title) {
          metadataYaml += `title: "${$selectedProject.title}"\n`;
        }
        if ($selectedProject.author) {
          metadataYaml += `author: "${$selectedProject.author}"\n`;
        }
        if ($selectedProject.description) {
          metadataYaml += `description: "${$selectedProject.description}"\n`;
        }
      }
      metadataYaml += '---\n';
      zip.file('metadata.yaml', metadataYaml);

      // === 4. Créer manifest.yaml à la racine du ZIP ===
      const filenameResults = generateFilenames($docs);

      // Compute global lastModified: max of all doc and template timestamps
      const allTimestamps: string[] = [];
      for (const r of filenameResults) {
        if (r.doc.updatedAt) allTimestamps.push(r.doc.updatedAt);
      }
      for (const tpl of allTemplates) {
        if (tpl.content && tpl.updatedAt) allTimestamps.push(tpl.updatedAt);
      }
      for (const img of $images) {
        if (img.updatedAt) allTimestamps.push(img.updatedAt);
      }
      const globalLastModified = allTimestamps.length
        ? allTimestamps.reduce((a, b) => (a > b ? a : b))
        : new Date().toISOString();

      let manifestYaml = 'version: 1\n';
      manifestYaml += `lastModified: '${globalLastModified}'\n`;
      manifestYaml += 'metadata: metadata.yaml\n';
      manifestYaml += '\ntexts:\n';

      for (const result of filenameResults) {
        const section = result.section || 'bodymatter';
        manifestYaml += `  - file: texts/${result.filename}\n`;
        manifestYaml += `    type: ${section}\n`;
        if (result.doc.updatedAt) {
          manifestYaml += `    lastModified: '${result.doc.updatedAt}'\n`;
        }
      }

      // Assets section
      const coverImage = $images.find(img => img.imageSection === 'cover');
      if (coverImage) {
        manifestYaml += '\nassets:\n';
        manifestYaml += `  cover: assets/${coverImage.filename}\n`;
      }

      manifestYaml += '\noutput: build\n';

      manifestYaml += '\ntemplate: template\n';

      manifestYaml += '\nconfig:\n';
      manifestYaml += '  mapping: config/mapping.json\n';
      manifestYaml += '  typography: config/typography.json\n';
      manifestYaml += '  pagelayout: config/pagelayout.json\n';
      manifestYaml += '  grep: config/grep.json\n';
      manifestYaml += '  export: config/export.json\n';
      manifestYaml += '  general: config/general.json\n';

      // Templates section — list all template files with lastModified
      const tplSections: Record<string, Array<{ file: string; lastModified?: string; documentType?: string }>> = {};
      const addTpl = (group: string, filePath: string, tpl: any) => {
        if (!tplSections[group]) tplSections[group] = [];
        const entry: { file: string; lastModified?: string; documentType?: string } = { file: filePath, lastModified: tpl.updatedAt };
        if (tpl.documentType) entry.documentType = tpl.documentType;
        tplSections[group].push(entry);
      };
      for (const tpl of allTemplates) {
        if (!tpl.content) continue;
        const s = tpl.templateSection;
        const t = tpl.templateType;
        if (s === 'indesign' && t === 'indesign' && tpl.filename?.endsWith('.indd')) {
          addTpl('indesign', `template/indesign/${tpl.filename}`, tpl);
        } else if (s === 'fonts' || t === 'font') {
          addTpl('epub', `template/epub/fonts/${tpl.filename}`, tpl);
        } else if (t === 'css' || s === 'epub2' || s === 'epub3') {
          addTpl('epub', `template/epub/style/${tpl.filename}`, tpl);
        } else if (t === 'lua' || s === 'luafilters') {
          addTpl('epub', `template/epub/lua/${tpl.filename}`, tpl);
        } else if (s === 'csl' || t === 'csl') {
          addTpl('csl', `template/csl/${tpl.filename}`, tpl);
        } else if (t === 'preparo' || s === 'preparo') {
          addTpl('preparo', `template/preparo/${tpl.filename}`, tpl);
        }
      }
      const sectionOrder = ['indesign', 'epub', 'csl', 'preparo'];
      const hasTemplates = sectionOrder.some(s => tplSections[s]?.length);
      if (hasTemplates) {
        manifestYaml += '\ntemplates:\n';
        for (const sec of sectionOrder) {
          const entries = tplSections[sec];
          if (!entries?.length) continue;
          manifestYaml += `  ${sec}:\n`;
          for (const entry of entries) {
            manifestYaml += `    - file: ${entry.file}\n`;
            if (entry.lastModified) {
              manifestYaml += `      lastModified: '${entry.lastModified}'\n`;
            }
            if (entry.documentType) {
              manifestYaml += `      documentType: ${entry.documentType}\n`;
            }
          }
        }
      }

      manifestYaml += '\noutputs:\n';
      manifestYaml += '  indesign: []\n';
      manifestYaml += '  epub: []\n';
      manifestYaml += '  pdf: []\n';

      // manifest.yaml à la racine du ZIP (pas dans manuscript/)
      zip.file('manifest.yaml', manifestYaml);

      // === 5. Ajouter les configs InDesign dans config/ ===
      const mappingTemplate = allTemplates.find((t: any) => t.filename === 'mapping.json');

      if (mappingTemplate?.content) {
        configFolder.file('mapping.json', mappingTemplate.content);
      } else {
        const defaultMapping = {
          h1: 'H1',
          h2: 'H2',
          h3: 'H3',
          h4: 'H4',
          h5: 'H5',
          h6: 'H6',
          quote: 'Citation',
          bulletlist: 'Bulleted list',
          normal: 'Body text',
          italic: 'Italic',
          bold: 'Bold',
          bolditalic: 'Bold italic',
          underline: 'Underline',
          smallcaps: 'Small caps',
          subscript: 'Subscript',
          superscript: 'Superscript',
          strikethrough: '',
          note: 'Footnote',
          removeBlankPages: true
        };
        configFolder.file('mapping.json', JSON.stringify(defaultMapping, null, '\t'));
      }

      // Typography config
      const typoTemplate = allTemplates.find((t: any) => t.filename === 'typography.json');
      if (typoTemplate?.content) {
        configFolder.file('typography.json', typoTemplate.content);
      }

      // PageLayout config
      const plTemplate = allTemplates.find((t: any) => t.filename === 'pagelayout.json');
      if (plTemplate?.content) {
        configFolder.file('pagelayout.json', plTemplate.content);
      }

      // Grep config
      const grepTemplate = allTemplates.find((t: any) => t.filename === 'grep.json');
      if (grepTemplate?.content) {
        configFolder.file('grep.json', grepTemplate.content);
      }

      // Export config (merge with defaults to ensure all fields are present)
      const defaultExportConfig = {
        indesignEnabled: true,
        pdfEnabled: false,
        epubEnabled: false,
        pdfFormat: 'print',
        pdfPreset: '',
        epubFormat: 'epub3'
      };
      const exportTemplate = allTemplates.find((t: any) => t.filename === 'export.json');
      if (exportTemplate?.content) {
        try {
          const merged = { ...defaultExportConfig, ...JSON.parse(exportTemplate.content) };
          configFolder.file('export.json', JSON.stringify(merged, null, '\t'));
        } catch {
          configFolder.file('export.json', exportTemplate.content);
        }
      } else {
        configFolder.file('export.json', JSON.stringify(defaultExportConfig, null, '\t'));
      }

      // General config
      const defaultGeneralConfig = {
        presetId: '',
        presetName: '',
        typographyEnabled: true,
        filePrefix: ''
      };
      const generalTemplate = allTemplates.find((t: any) => t.filename === 'general.json');
      if (generalTemplate?.content) {
        try {
          const merged = { ...defaultGeneralConfig, ...JSON.parse(generalTemplate.content) };
          configFolder.file('general.json', JSON.stringify(merged, null, '\t'));
        } catch {
          configFolder.file('general.json', generalTemplate.content);
        }
      } else {
        configFolder.file('general.json', JSON.stringify(defaultGeneralConfig, null, '\t'));
      }

      // === 6. Ajouter les templates EPUB ===
      // Fonts (templateSection === 'fonts' ou templateType === 'font')
      const fontTemplates = allTemplates.filter(
        (t: any) => t.templateSection === 'fonts' || t.templateType === 'font'
      );
      for (const font of fontTemplates) {
        if (templateEpubFontsFolder && font.content) {
          templateEpubFontsFolder.file(font.filename, font.content, { base64: true });
        }
      }

      // CSS stylesheets (templateSection === 'epub2' ou 'epub3' ou templateType === 'css')
      const cssTemplates = allTemplates.filter(
        (t: any) => t.templateType === 'css' || t.templateSection === 'epub2' || t.templateSection === 'epub3'
      );
      for (const css of cssTemplates) {
        if (templateEpubStyleFolder && css.content) {
          templateEpubStyleFolder.file(css.filename, css.content);
        }
      }

      // Lua filters (templateType === 'lua' ou templateSection === 'luafilters')
      const luaTemplates = allTemplates.filter(
        (t: any) => t.templateType === 'lua' || t.templateSection === 'luafilters'
      );
      for (const lua of luaTemplates) {
        if (templateEpubLuaFolder && lua.content) {
          templateEpubLuaFolder.file(lua.filename, lua.content);
        }
      }

      // === 7. Ajouter les templates Preparo ===
      const preparoTemplates = allTemplates.filter(
        (t: any) => t.templateType === 'preparo' || t.templateSection === 'preparo'
      );
      for (const tpl of preparoTemplates) {
        if (templatePreparoFolder && tpl.content) {
          templatePreparoFolder.file(tpl.filename, tpl.content);
        }
      }

      // === 8. Ajouter les templates CSL (Citation Style Language) ===
      const cslTemplates = allTemplates.filter(
        (t: any) => t.templateSection === 'csl' || t.templateType === 'csl'
      );
      for (const tpl of cslTemplates) {
        if (templateCslFolder && tpl.content) {
          templateCslFolder.file(tpl.filename, tpl.content);
        }
      }

      // === 9. Ajouter les templates InDesign (.indd) ===
      const indesignTemplates = allTemplates.filter(
        (t: any) => t.templateSection === 'indesign' && t.templateType === 'indesign'
      );
      for (const tpl of indesignTemplates) {
        if (templateIndesignFolder && tpl.content && tpl.filename?.endsWith('.indd')) {
          templateIndesignFolder.file(tpl.filename, tpl.content, { base64: true });
        }
      }

      // === 10. Générer le ZIP et déclencher le téléchargement ===
      const blob = await zip.generateAsync({ type: 'blob' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = $selectedProject.title
        ? `${$selectedProject.title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')}.zip`
        : 'project.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('ZIP export failed:', error);
      alert($i18n('docs.exportError', { message: (error as Error).message }));
    } finally {
      isExporting = false;
    }
  }

  // Changer le tri (reserved for future use)
  function _handleSort(column: SortColumn): void {
    appActions.setSortColumn(column);
  }

  // Menu contextuel
  async function handleContextMenu(event: MouseEvent, doc: Document): Promise<void> {
    event.preventDefault();

    // If the doc is not in the selection, select only it
    if (!selectedDocs.has(doc.id)) {
      selectedDocs = new Set([doc.id]);
      lastSelectedDocId = doc.id;
    }

    contextMenuDoc = doc;
    contextMenuPosition = { x: event.clientX, y: event.clientY };
    showTypeSubmenu = false;

    // Attendre le rendu du menu puis ajuster la position si nécessaire
    await tick();
    adjustContextMenuPosition();
  }

  // Ajuster la position du menu contextuel pour qu'il reste dans la fenêtre
  function adjustContextMenuPosition(): void {
    if (!contextMenuElement) return;

    const rect = contextMenuElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 8; // Marge de sécurité

    let { x, y } = contextMenuPosition;

    // Si le menu dépasse en bas, le remonter
    if (rect.bottom > viewportHeight - padding) {
      y = viewportHeight - rect.height - padding;
    }

    // Si le menu dépasse en haut (cas rare), le descendre
    if (y < padding) {
      y = padding;
    }

    // Si le menu dépasse à droite, le décaler à gauche
    if (rect.right > viewportWidth - padding) {
      x = viewportWidth - rect.width - padding;
    }

    // Si le menu dépasse à gauche, le décaler à droite
    if (x < padding) {
      x = padding;
    }

    // Mettre à jour la position si elle a changé
    if (x !== contextMenuPosition.x || y !== contextMenuPosition.y) {
      contextMenuPosition = { x, y };
    }
  }

  function closeContextMenu(): void {
    contextMenuDoc = null;
    showTypeSubmenu = false;
  }

  // Toggle type submenu
  function toggleTypeSubmenu(): void {
    showTypeSubmenu = !showTypeSubmenu;
  }

  // Set document type for all selected documents
  async function handleSetDocumentType(docType: DocumentType | null): Promise<void> {
    if (selectedDocs.size === 0) return;

    // Determine the section based on document type
    let section: 'frontmatter' | 'bodymatter' | 'backmatter' | null = null;
    if (docType) {
      section = getSectionFromType(docType);
    }

    try {
      // Update all selected documents metadata
      const updatePromises = Array.from(selectedDocs).map(docId =>
        appActions.updateDoc(docId, {
          documentType: docType,
          documentSection: section
        })
      );
      await Promise.all(updatePromises);

      // Write shorttitle in YAML frontmatter for each selected document
      if (docType) {
        // Get project language for translation
        const meta = parseProjectMetadata($selectedProject?.metadata);
        const langCode = meta.lang?.slice(0, 2)?.toLowerCase();
        const lang: LanguageCode = (langCode === 'en' || langCode === 'fr') ? langCode as LanguageCode : 'fr';
        const typeLabel = translations[lang]?.[`docType.${docType}`] || translations['en']?.[`docType.${docType}`] || docType;

        // Count how many docs of this type exist now (including just-updated ones)
        const allDocsOfType = $docs
          .filter(d => {
            if (selectedDocs.has(d.id)) return true; // just assigned
            return d.documentType === docType;
          })
          .sort((a, b) => {
            const sectionOrder = ['frontmatter', 'bodymatter', 'backmatter'];
            const sA = sectionOrder.indexOf(a.documentSection || 'bodymatter');
            const sB = sectionOrder.indexOf(b.documentSection || 'bodymatter');
            if (sA !== sB) return sA - sB;
            const oA = a.order ?? Infinity;
            const oB = b.order ?? Infinity;
            if (oA !== oB) return oA - oB;
            return (a.title || '').localeCompare(b.title || '');
          });

        const needsNumber = allDocsOfType.length > 1;

        // Update frontmatter and filename for each selected doc
        const fmPromises = Array.from(selectedDocs).map(async (docId) => {
          const rawContent = await db.contents.get(docId) || '';
          const occurrence = needsNumber ? allDocsOfType.findIndex(d => d.id === docId) + 1 : 0;
          const shorttitle = needsNumber ? `${typeLabel} ${occurrence}` : typeLabel;

          // Generate stable filename from documentType (e.g. "chapter-1.md", "titlepage.md")
          const gitFilename = needsNumber ? `${docType}-${occurrence}.md` : `${docType}.md`;
          await appActions.updateDoc(docId, { filename: gitFilename });

          // Check if content already has frontmatter
          const fmMatch = rawContent.match(/^---\n([\s\S]*?\n)(?:---|\.\.\.)\n?/);
          let newContent: string;

          if (fmMatch) {
            const fm = fmMatch[0].endsWith('\n') ? fmMatch[0] : fmMatch[0] + '\n';
            const body = rawContent.slice(fmMatch[0].length).replace(/^\n/, '');
            let newFm: string;
            if (/^shorttitle:\s*.*$/m.test(fm)) {
              // Check if current value is auto-generated
              const currentValue = fm.match(/^shorttitle:\s*(.*)$/m)?.[1]?.trim().replace(/^['"]|['"]$/g, '') || '';
              const baseValue = currentValue.replace(/\s+\d+$/, '');
              const isAuto = !currentValue || Object.values(translations).some(t =>
                Object.keys(t).some(k => k.startsWith('docType.') && t[k] === baseValue)
              );
              newFm = isAuto ? fm.replace(/^shorttitle:\s*.*$/m, `shorttitle: ${shorttitle}`) : fm;
            } else {
              newFm = fm.replace(/^(---\n?)$/m, `shorttitle: ${shorttitle}\n$1`);
            }
            newContent = newFm + '\n' + body;
          } else {
            // Create frontmatter from scratch
            newContent = `---\nshorttitle: ${shorttitle}\n---\n\n${rawContent}`;
          }

          // Apply epub:type attributes to first H1
          newContent = applyEpubTypeToMarkdown(newContent, docType);

          await db.contents.set(docId, newContent);
        });

        await Promise.all(fmPromises);
      }

      // Show toast notification
      const count = selectedDocs.size;
      showToast(count === 1
        ? `Type défini: ${docType || 'aucun'}`
        : `${count} documents mis à jour`
      );
    } catch (error) {
      console.error('Failed to update document types:', error);
    }

    closeContextMenu();
  }

  // Actions du menu contextuel
  async function handleChangeStatus(newStatus: DocumentStatus): Promise<void> {
    if (selectedDocs.size === 0 && !contextMenuDoc) return;

    try {
      // Update all selected documents
      const docsToUpdate = selectedDocs.size > 0 ? Array.from(selectedDocs) : (contextMenuDoc ? [contextMenuDoc.id] : []);
      const updatePromises = docsToUpdate.map(docId =>
        appActions.updateDoc(docId, { status: newStatus })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Failed to update doc status:', error);
    }

    closeContextMenu();
  }

  async function handleEditDoc(): Promise<void> {
    if (!contextMenuDoc) return;

    const newTitle = prompt($i18n('docs.editPrompt'), contextMenuDoc.title);
    if (newTitle && newTitle.trim()) {
      try {
        await appActions.updateDoc(contextMenuDoc.id, {
          title: newTitle.trim()
        });
      } catch (error) {
        console.error('Failed to update doc:', error);
      }
    }

    closeContextMenu();
  }

  async function handleDeleteDoc(): Promise<void> {
    if (!contextMenuDoc) return;

    // Si plusieurs documents sont sélectionnés, les supprimer tous
    if (selectedDocs.size > 1) {
      const confirmed = confirm($i18n('docs.deleteMultipleConfirm', { count: selectedDocs.size }));
      if (confirmed) {
        try {
          const deletePromises = Array.from(selectedDocs).map(docId => appActions.deleteDoc(docId));
          await Promise.all(deletePromises);
          selectedDocs = new Set(); // Vider la sélection
        } catch (error) {
          console.error('Failed to delete docs:', error);
        }
      }
    } else {
      // Supprimer un seul document
      const confirmed = confirm($i18n('docs.deleteConfirm', { title: contextMenuDoc.title }));
      if (confirmed) {
        try {
          await appActions.deleteDoc(contextMenuDoc.id);
        } catch (error) {
          console.error('Failed to delete doc:', error);
        }
      }
    }

    closeContextMenu();
  }

  // Fermer les menus en cliquant ailleurs
  function handleClickOutside(event: MouseEvent): void {
    if (contextMenuDoc) {
      closeContextMenu();
    }
    if (showExportMenu) {
      const target = event.target as HTMLElement;
      if (!target.closest('.export-dropdown')) {
        showExportMenu = false;
      }
    }
  }

  // ============================================================================
  // RÉORDONNANCEMENT DES DOCUMENTS
  // ============================================================================

  // État du drag & drop
  let draggedDoc: Document | null = null;
  let dragOverDoc: Document | null = null;
  let dragOverSection: SectionId | null = null;

  // Déplacer un document vers le haut dans sa section
  async function handleMoveUp(): Promise<void> {
    if (!contextMenuDoc) return;

    const section = (contextMenuDoc.documentSection || (contextMenuDoc.documentType ? 'bodymatter' : 'undefined')) as SectionId;
    const sectionDocs = groupedDocs[section];
    const currentIndex = sectionDocs.findIndex(d => d.id === contextMenuDoc!.id);

    if (currentIndex <= 0) {
      closeContextMenu();
      return; // Déjà en haut
    }

    await swapDocumentOrder(sectionDocs[currentIndex], sectionDocs[currentIndex - 1]);
    closeContextMenu();
  }

  // Déplacer un document vers le bas dans sa section
  async function handleMoveDown(): Promise<void> {
    if (!contextMenuDoc) return;

    const section = (contextMenuDoc.documentSection || (contextMenuDoc.documentType ? 'bodymatter' : 'undefined')) as SectionId;
    const sectionDocs = groupedDocs[section];
    const currentIndex = sectionDocs.findIndex(d => d.id === contextMenuDoc!.id);

    if (currentIndex >= sectionDocs.length - 1) {
      closeContextMenu();
      return; // Déjà en bas
    }

    await swapDocumentOrder(sectionDocs[currentIndex], sectionDocs[currentIndex + 1]);
    closeContextMenu();
  }

  // Échanger l'ordre de deux documents
  async function swapDocumentOrder(docA: Document, docB: Document): Promise<void> {
    // Determine the section to find actual indices
    const section = (docA.documentSection || (docA.documentType ? 'bodymatter' : 'undefined')) as SectionId;
    const sectionDocs = groupedDocs[section];
    const indexA = sectionDocs.findIndex(d => d.id === docA.id);
    const indexB = sectionDocs.findIndex(d => d.id === docB.id);

    // Use existing order values, falling back to index position
    const orderA = docA.order ?? indexA;
    const orderB = docB.order ?? indexB;

    // If both have the same order value (e.g. both undefined → same index fallback),
    // assign distinct sequential values based on their positions
    const newOrderA = orderA === orderB ? indexB : orderB;
    const newOrderB = orderA === orderB ? indexA : orderA;

    try {
      await Promise.all([
        appActions.updateDoc(docA.id, { order: newOrderA }),
        appActions.updateDoc(docB.id, { order: newOrderB })
      ]);
      console.log(`🔄 Swapped order: ${docA.title} (${newOrderA}) <-> ${docB.title} (${newOrderB})`);
    } catch (error) {
      console.error('Failed to swap document order:', error);
    }
  }

  // Drag & Drop handlers
  function handleDragStart(event: DragEvent, doc: Document): void {
    draggedDoc = doc;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', doc.id);
    }
    // Ajouter une classe pour le style
    (event.target as HTMLElement).classList.add('dragging');
  }

  function handleDragEnd(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('dragging');
    draggedDoc = null;
    dragOverDoc = null;
    dragOverSection = null;
  }

  function handleDragOver(event: DragEvent, doc: Document, sectionId: SectionId): void {
    event.preventDefault();

    // Vérifier que le drag est dans la même section
    const draggedSection = draggedDoc?.documentSection || (draggedDoc?.documentType ? 'bodymatter' : 'undefined');
    if (draggedSection !== sectionId) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      return;
    }

    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    dragOverDoc = doc;
    dragOverSection = sectionId;
  }

  function handleDragLeave(event: DragEvent): void {
    // Ne réinitialiser que si on quitte vraiment l'élément
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node)) {
      dragOverDoc = null;
    }
  }

  async function handleDrop(event: DragEvent, targetDoc: Document, sectionId: SectionId): Promise<void> {
    event.preventDefault();

    if (!draggedDoc || draggedDoc.id === targetDoc.id) {
      draggedDoc = null;
      dragOverDoc = null;
      return;
    }

    // Vérifier que le drop est dans la même section
    const draggedSection = draggedDoc.documentSection || (draggedDoc.documentType ? 'bodymatter' : 'undefined');
    if (draggedSection !== sectionId) {
      draggedDoc = null;
      dragOverDoc = null;
      return;
    }

    // Réordonner les documents
    const sectionDocs = [...groupedDocs[sectionId]];
    const draggedIndex = sectionDocs.findIndex(d => d.id === draggedDoc!.id);
    const targetIndex = sectionDocs.findIndex(d => d.id === targetDoc.id);

    // Retirer le document déplacé et l'insérer à la nouvelle position
    sectionDocs.splice(draggedIndex, 1);
    sectionDocs.splice(targetIndex, 0, draggedDoc);

    // Mettre à jour l'ordre de tous les documents de la section
    try {
      await Promise.all(
        sectionDocs.map((doc, index) =>
          appActions.updateDoc(doc.id, { order: index })
        )
      );
      console.log(`✅ Reordered ${sectionDocs.length} documents in ${sectionId}`);
    } catch (error) {
      console.error('Failed to reorder documents:', error);
    }

    draggedDoc = null;
    dragOverDoc = null;
  }

  // Vérifier si un document peut être déplacé vers le haut/bas
  function canMoveUp(doc: Document): boolean {
    const section = (doc.documentSection || (doc.documentType ? 'bodymatter' : 'undefined')) as SectionId;
    const sectionDocs = groupedDocs[section];
    const currentIndex = sectionDocs.findIndex(d => d.id === doc.id);
    return currentIndex > 0;
  }

  function canMoveDown(doc: Document): boolean {
    const section = (doc.documentSection || (doc.documentType ? 'bodymatter' : 'undefined')) as SectionId;
    const sectionDocs = groupedDocs[section];
    const currentIndex = sectionDocs.findIndex(d => d.id === doc.id);
    return currentIndex < sectionDocs.length - 1;
  }

  // Formater la date
  function formatDate(date: string | Date | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  // ============================================================================
  // IMAGE HANDLING
  // ============================================================================

  /**
   * Read file as data URL
   */
  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get image dimensions from data URL
   */
  function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
  }

  /**
   * Delete an image
   */
  async function handleDeleteImage(image: Image): Promise<void> {
    if (!confirm($i18n('images.confirmDelete', { filename: image.filename }))) {
      return;
    }

    try {
      await appActions.deleteImage(image.id);
      if (selectedImage?.id === image.id) {
        selectedImage = null;
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert($i18n('images.deleteError', { message: (error as Error).message }));
    }
  }

  /**
   * Show a toast notification
   */
  function showToast(message: string): void {
    toastMessage = message;
    toastVisible = true;
    setTimeout(() => {
      toastVisible = false;
    }, 2000);
  }

  /**
   * Copy image markdown reference to clipboard
   */
  async function copyImageMarkdown(image: Image): Promise<void> {
    const markdown = `![${image.altText || image.filename}](${image.dataUrl})`;
    try {
      await navigator.clipboard.writeText(markdown);
      showToast($i18n('images.copied'));
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }

  /**
   * Handle drag start for images - allows dragging to editor
   */
  function handleImageDragStart(event: DragEvent, image: Image): void {
    if (!event.dataTransfer) return;

    // Set the drag data with image info (only custom MIME type, no text/plain to avoid double insertion)
    event.dataTransfer.setData('application/x-preparo-image', JSON.stringify({
      filename: image.filename,
      altText: image.altText || image.filename
    }));
    event.dataTransfer.effectAllowed = 'copy';
  }

  /**
   * Format file size for display
   */
  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ============================================================================
  // TEMPLATE HANDLING
  // ============================================================================

  /**
   * Determine template type from file extension and MIME type
   */
  function getTemplateType(filename: string, mimeType: string): TemplateType {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'css' || mimeType === 'text/css') {
      return 'css';
    }
    if (['ttf', 'otf', 'woff', 'woff2'].includes(ext || '') ||
        mimeType.includes('font')) {
      return 'font';
    }
    return 'other';
  }

  /**
   * Read file as text
   */
  function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Read file as base64
   */
  function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 data (remove data URL prefix)
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Delete a template
   */
  function handleSelectTemplate(template: Template): void {
    // Toggle selection
    if (selectedTemplate?.id === template.id) {
      selectedTemplate = null;
      editingTemplate.set(null);
      return;
    }
    selectedTemplate = template;

    // Load text-based templates into the editor (preparo, css, lua, csl)
    // Font files are binary and cannot be edited
    if (template.templateType !== 'font') {
      selectedDoc.set(null);
      editingTemplate.set(template);
      editorActions.loadTemplate(template);
    }
  }

  async function handleDeleteTemplate(template: Template): Promise<void> {
    if (!confirm($i18n('templates.confirmDelete', { filename: template.filename }))) {
      return;
    }

    try {
      await appActions.deleteTemplate(template.id);
      if (selectedTemplate?.id === template.id) {
        selectedTemplate = null;
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert($i18n('templates.deleteError', { message: (error as Error).message }));
    }
  }

  /**
   * Get icon for template type
   */
  function getTemplateIcon(templateType: TemplateType): string {
    switch (templateType) {
      case 'css': return '🎨';
      case 'font': return '🔤';
      case 'preparo': return '📝';
      case 'lua': return '🌙';
      case 'csl': return '📚';
      case 'indesign': return '📐';
      default: return '📄';
    }
  }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="docs-panel">
  <!-- Header -->
  <div class="panel-header">
    <div class="header-left">
      <h2 class="panel-title">
        {#if $selectedProject}
          <span class="project-badge">{$selectedProject.title}</span>
        {/if}
      </h2>
    </div>
    <div class="header-actions">
      <button
        class="btn-icon btn-import"
        on:click={() => showImporter = true}
        disabled={!$selectedProject}
        title={$selectedProject ? $i18n('docs.import') : $i18n('docs.selectProjectFirst')}
      >
        📥
      </button>
      <div class="export-dropdown">
        <button
          class="btn-icon btn-export"
          on:click={() => showExportMenu = !showExportMenu}
          disabled={!$selectedProject || isExporting}
          title={$selectedProject ? $i18n('docs.export') : $i18n('docs.selectProjectFirst')}
        >
          {isExporting ? '⏳' : '📤'}
        </button>
        {#if showExportMenu}
          <div class="export-menu">
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportEpub('epub3'); }}>
              📚 {$i18n('docs.exportEpub3')}
            </button>
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportEpub('epub2'); }}>
              📚 {$i18n('docs.exportEpub2')}
            </button>
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportZip(); }}>
              📁 {$i18n('docs.exportZip')}
            </button>
            <hr class="export-separator" />
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportFormat('docx'); }}>
              📝 {$i18n('docs.exportDocx')}
            </button>
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportFormat('odt'); }}>
              📄 {$i18n('docs.exportOdt')}
            </button>
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportFormat('pdf'); }}>
              📕 {$i18n('docs.exportPdf')}
            </button>
            <button class="export-option" on:click={() => { showExportMenu = false; handleExportFormat('html'); }}>
              🌐 {$i18n('docs.exportHtml')}
            </button>
          </div>
        {/if}
      </div>
      <button
        class="btn-icon btn-config"
        on:click={() => showConfigPanel = true}
        disabled={!$selectedProject}
        title={$selectedProject ? 'Configuration du projet' : $i18n('docs.selectProjectFirst')}
      >
        ⚙️
      </button>
      <button
        class="btn-icon btn-add"
        on:click={() => {
          if (activeTab === 'templates') {
            showNewTemplateForm = !showNewTemplateForm;
            showNewDocForm = false;
          } else if (activeTab === 'text') {
            showNewDocForm = !showNewDocForm;
            showNewTemplateForm = false;
          }
        }}
        disabled={!$selectedProject || activeTab === 'images'}
        title={activeTab === 'templates' ? 'New template' : (activeTab === 'text' ? $i18n('docs.newDoc') : '')}
      >
        {(activeTab === 'text' && showNewDocForm) || (activeTab === 'templates' && showNewTemplateForm) ? '✕' : '➕'}
      </button>
    </div>
  </div>

  <!-- Tabs -->
  <div class="panel-tabs">
    {#if !isArticle}
    <button
      class="tab-button"
      class:active={activeTab === 'text'}
      on:click={() => activeTab = 'text'}
    >
      📄 {$i18n('docs.tabText')}
      <span class="tab-count">{$docs.length}</span>
    </button>
    {/if}
    <button
      class="tab-button"
      class:active={activeTab === 'images'}
      on:click={() => activeTab = 'images'}
    >
      🖼️ {$i18n('docs.tabImages')}
      <span class="tab-count">{$images.length}</span>
    </button>
    <button
      class="tab-button"
      class:active={activeTab === 'templates'}
      on:click={() => activeTab = 'templates'}
    >
      📎 {$i18n('docs.tabTemplates')}
      <span class="tab-count">{templateTopSectionCount}</span>
    </button>
  </div>

  {#if activeTab === 'text'}
  <!-- Barre de recherche et filtres -->
  <div class="toolbar">
    <div class="search-bar">
      <input
        type="text"
        class="search-input"
        placeholder="🔍 {$i18n('docs.searchPlaceholder')}"
        bind:value={localSearchQuery}
      />
    </div>

    <div class="filters">
      <select
        class="filter-select"
        bind:value={statusFilter}
      >
        <option value="all">{$i18n('docs.filterAll')}</option>
        <option value="todo">{$i18n('docs.statusTodo')}</option>
        <option value="in-progress">{$i18n('docs.statusInProgress')}</option>
        <option value="done">{$i18n('docs.statusDone')}</option>
      </select>
    </div>
  </div>

  <!-- Formulaire nouveau document -->
  {#if showNewDocForm}
    <div class="new-doc-form">
      <input
        type="text"
        class="form-input"
        placeholder={$i18n('docs.docTitle')}
        bind:value={newDocTitle}
        on:keydown={(e) => e.key === 'Enter' && handleCreateDoc()}
        autofocus
      />
      <select
        class="form-select document-type-selector"
        bind:value={newDocType}
        on:change={() => {
          if (newDocType && !newDocTitle.trim()) {
            newDocTitle = $i18n(`docType.${newDocType}`);
          }
        }}
      >
        <option value="">Type (optional)</option>
        {#each Object.entries(DOCUMENT_TYPES) as [category, data]}
          <optgroup label={data.label}>
            {#each data.types as type}
              <option value={type.value}>{type.label}</option>
            {/each}
          </optgroup>
        {/each}
      </select>
      <div class="form-actions">
        <button
          class="btn btn-primary"
          on:click={handleCreateDoc}
          disabled={!newDocTitle.trim()}
        >
          {$i18n('docs.create')}
        </button>
        <button
          class="btn btn-secondary"
          on:click={handleCancelCreate}
        >
          {$i18n('docs.cancel')}
        </button>
      </div>
    </div>
  {/if}

  <!-- Liste des documents par section -->
  <div class="docs-list">
    {#if $loadingState.docs}
      <div class="loading-state">
        <div class="spinner"></div>
        <p>{$i18n('docs.loading')}</p>
      </div>
    {:else if $errors.docs}
      <div class="error-state">
        <p>❌ {$errors.docs}</p>
        <button
          class="btn btn-primary"
          on:click={() => appActions.loadDocs($selectedProject?.id)}
        >
          {$i18n('docs.retry')}
        </button>
      </div>
    {:else if !$selectedProject}
      <div class="empty-state">
        <div class="empty-icon">📂</div>
        <p>{$i18n('docs.selectProjectFirst')}</p>
      </div>
    {:else if filteredByStatus.length === 0}
      <div class="empty-state">
        {#if localSearchQuery}
          <p>{$i18n('docs.noDocsFound')}</p>
        {:else if statusFilter !== 'all'}
          <p>{$i18n('docs.noDocsFound')}</p>
        {:else}
          <p>{$i18n('docs.noDocsYet')}</p>
          <button
            class="btn btn-primary"
            on:click={() => showNewDocForm = true}
          >
            {$i18n('docs.createFirst')}
          </button>
        {/if}
      </div>
    {:else}
      {#each sectionOrder as sectionId}
        {@const sectionDocs = groupedDocs[sectionId]}
        {@const section = sections[sectionId]}
        <!-- Section 'undefined' n'apparaît que s'il y a des documents sans type -->
        {#if sectionId === 'undefined' ? sectionDocs.length > 0 : (sectionDocs.length > 0 || !localSearchQuery)}
          <div class="section-group" class:section-undefined={sectionId === 'undefined'}>
            <button
              class="section-header"
              class:collapsed={collapsedSections[sectionId]}
              on:click={() => toggleSection(sectionId)}
            >
              <span class="section-toggle">
                {collapsedSections[sectionId] ? '▶' : '▼'}
              </span>
              <span class="section-icon">{section.icon}</span>
              <span class="section-title">{$i18n(`docs.section.${sectionId}`)}</span>
              <span class="section-count">{sectionDocs.length}</span>
            </button>

            {#if !collapsedSections[sectionId]}
              <div class="section-docs">
                {#if sectionDocs.length === 0}
                  <div class="section-empty">
                    <span class="section-empty-text">{$i18n(`docs.section.${sectionId}Desc`)}</span>
                  </div>
                {:else}
                  {#each sectionDocs as doc, index (doc.id)}
                    {@const filename = getDocumentFilename(doc, $docs)}
                    <div
                      class="doc-item"
                      class:selected={$selectedDoc?.id === doc.id}
                      class:multi-selected={selectedDocs.has(doc.id)}
                      class:drag-over={dragOverDoc?.id === doc.id}
                      draggable="true"
                      on:click={(e) => handleSelectDoc(doc, e)}
                      on:contextmenu={(e) => handleContextMenu(e, doc)}
                      on:dragstart={(e) => handleDragStart(e, doc)}
                      on:dragend={handleDragEnd}
                      on:dragover={(e) => handleDragOver(e, doc, sectionId)}
                      on:dragleave={handleDragLeave}
                      on:drop={(e) => handleDrop(e, doc, sectionId)}
                    >
                      <div class="doc-order">{index + 1}</div>
                      <div
                        class="doc-status"
                        style="color: {statusColors[doc.status]}"
                      >
                        {statusIcons[doc.status]}
                      </div>
                      <div class="doc-info">
                        <div class="doc-title">{doc.title.replace(/\s*\{[^}]+\}\s*$/, '')}</div>
                        {#if doc.wordCount || doc.charCount || filename}
                          <div class="doc-stats">
                            {#if filename}
                              <span class="doc-filename" title={filename}>{filename}</span>
                            {/if}
                            {#if filename && (doc.wordCount || doc.charCount)}
                              <span class="separator">·</span>
                            {/if}
                            {#if doc.wordCount}
                              <span>{$i18n('docs.words', { count: doc.wordCount })}</span>
                            {/if}
                            {#if doc.wordCount && doc.charCount}
                              <span class="separator">·</span>
                            {/if}
                            {#if doc.charCount}
                              <span>{$i18n('docs.chars', { count: doc.charCount })}</span>
                            {/if}
                          </div>
                        {/if}
                      </div>
                      <div class="doc-meta">
                        <div class="doc-date">{formatDate(doc.createdAt)}</div>
                      </div>
                    </div>
                  {/each}
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      {/each}
    {/if}
  </div>
  {/if}

  <!-- Images Tab Content -->
  {#if activeTab === 'images'}
  <div class="images-tab">
    <!-- Images by Section -->
    {#if $images.length === 0}
      <div class="empty-state">
        <div class="empty-icon">🖼️</div>
        <p>{$i18n('images.empty')}</p>
        <p class="empty-hint">{$i18n('images.emptyHint')}</p>
      </div>
    {:else}
      <div class="images-list">
        {#each imageSectionOrder as sectionId}
          {@const sectionImages = groupedImages[sectionId]}
          {@const section = imageSections[sectionId]}
          <div
            class="image-section-group"
            class:section-cover={sectionId === 'cover'}
            class:drag-over={dragOverImageSection === sectionId}
            on:dragover={(e) => handleImageSectionDragOver(e, sectionId)}
            on:dragleave={handleImageSectionDragLeave}
            on:drop={(e) => handleImageSectionDrop(e, sectionId)}
          >
            <button
              class="image-section-header"
              class:collapsed={collapsedImageSections[sectionId]}
              on:click={() => toggleImageSection(sectionId)}
            >
              <span class="section-toggle">
                {collapsedImageSections[sectionId] ? '▶' : '▼'}
              </span>
              <span class="section-icon">{section.icon}</span>
              <span class="section-title">{section.label}</span>
              <span class="section-count">{sectionImages.length}</span>
            </button>

            {#if !collapsedImageSections[sectionId]}
              <div class="image-section-content">
                {#if sectionImages.length === 0}
                  <div class="section-empty">
                    <span class="section-empty-text">{section.description}</span>
                  </div>
                {:else}
                  <div class="images-grid">
                    {#each sectionImages as image (image.id)}
                      <div
                        class="image-card"
                        class:selected={selectedImage?.id === image.id}
                        on:click={() => selectedImage = selectedImage?.id === image.id ? null : image}
                        draggable="true"
                        on:dragstart={(e) => handleImageSectionDragStart(e, image)}
                        on:dragend={handleImageSectionDragEnd}
                      >
                        <div class="image-preview">
                          <img src={image.dataUrl} alt={image.altText || image.filename} />
                        </div>
                        <div class="image-info">
                          <div class="image-filename" title={image.filename}>
                            {image.filename}
                          </div>
                          <div class="image-meta">
                            {#if image.width && image.height}
                              <span>{image.width}×{image.height}</span>
                              <span class="separator">·</span>
                            {/if}
                            <span>{formatFileSize(image.size)}</span>
                          </div>
                        </div>
                        <div class="image-actions">
                          <button
                            class="btn-icon"
                            on:click|stopPropagation={() => copyImageMarkdown(image)}
                            title={$i18n('images.copyMarkdown')}
                          >
                            📋
                          </button>
                          <button
                            class="btn-icon danger"
                            on:click|stopPropagation={() => handleDeleteImage(image)}
                            title={$i18n('images.delete')}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </div>
  {/if}

  <!-- Templates Tab Content -->
  {#if activeTab === 'templates'}
  <div class="templates-tab">
    <!-- Formulaire nouveau template Preparo -->
    {#if showNewTemplateForm}
      <div class="new-template-form">
        <select
          class="form-select document-type-selector"
          bind:value={newTemplateDocType}
        >
          <option value="">Select document type</option>
          {#each Object.entries(DOCUMENT_TYPES) as [category, data]}
            <optgroup label={data.label}>
              {#each data.types as type}
                <option value={type.value}>{type.label}</option>
              {/each}
            </optgroup>
          {/each}
        </select>
        <div class="form-actions">
          <button
            class="btn btn-primary"
            on:click={handleCreateMarkdownTemplate}
            disabled={!newTemplateDocType}
          >
            Create
          </button>
          <button
            class="btn btn-secondary"
            on:click={handleCancelTemplateCreate}
          >
            {$i18n('docs.cancel')}
          </button>
        </div>
      </div>
    {/if}
    <!-- Templates List by Section -->
    {#if effectiveTemplates.length === 0 && !showNewTemplateForm}
      <div class="empty-state">
        <div class="empty-icon">📎</div>
        <p>{$i18n('templates.empty')}</p>
        <p class="empty-hint">{$i18n('templates.emptyHint')}</p>
      </div>
    {:else}
      <div class="templates-list">
        <!-- Preparo group - Independent section for document templates -->
        <div class="template-preparo-group">
          <button
            class="template-section-header template-preparo-header"
            class:collapsed={preparoGroupCollapsed}
            on:click={() => preparoGroupCollapsed = !preparoGroupCollapsed}
          >
            <span class="section-toggle">
              {preparoGroupCollapsed ? '▶' : '▼'}
            </span>
            <span class="section-icon">📝</span>
            <span class="section-title">Preparo</span>
            <span class="section-count">{preparoTemplateCount}</span>
          </button>

          {#if !preparoGroupCollapsed}
            <div class="template-preparo-content">
              {#if groupedTemplates.preparo?.length === 0}
                <div class="section-empty">
                  <span class="section-empty-text">Preparo document templates (Markdown)</span>
                  <span class="section-empty-hint">Create templates for each document type (chapter, preface, bibliography...)</span>
                </div>
              {:else}
                {#each groupedTemplates.preparo as template (template.id)}
                  <div
                    class="template-item"
                    class:selected={selectedTemplate?.id === template.id}
                    on:click={() => handleSelectTemplate(template)}
                  >
                    <div class="template-icon">📝</div>
                    <div class="template-info">
                      <div class="template-filename" title={template.filename}>
                        {template.filename}
                      </div>
                      <div class="template-meta">
                        <span class="template-type">PREPARO</span>
                        {#if presetTemplateIds.has(template.id)}
                          <span class="separator">·</span>
                          <span class="preset-tag">PRESET</span>
                        {/if}
                        {#if template.documentType}
                          <span class="separator">·</span>
                          <span class="doc-type-badge">{template.documentType}</span>
                        {:else}
                          <span class="separator">·</span>
                          <span>{formatFileSize(template.size)}</span>
                        {/if}
                      </div>
                    </div>
                    <div class="template-actions">
                      <button
                        class="btn-icon danger"
                        on:click|stopPropagation={() => handleDeleteTemplate(template)}
                        title={$i18n('templates.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </div>

        <!-- ePub parent group -->
        <div class="template-epub-group">
          <button
            class="template-epub-header"
            class:collapsed={epubGroupCollapsed}
            on:click={() => epubGroupCollapsed = !epubGroupCollapsed}
          >
            <span class="section-toggle">
              {epubGroupCollapsed ? '▶' : '▼'}
            </span>
            <span class="section-icon">📦</span>
            <span class="section-title">ePub</span>
            <span class="section-count">{epubTemplateCount}</span>
          </button>

          {#if !epubGroupCollapsed}
            <div class="template-epub-content">
              <!-- Subsections in alphabetical order: Filtre Lua, Font, Style -->

              <!-- 1. Filtre Lua -->
              {#each epubSubsectionOrder as sectionId}
                {@const sectionTemplates = groupedTemplates[sectionId]}
                {@const section = templateSections[sectionId]}
                <div
                  class="template-section-group"
                  class:drag-over={dragOverTemplateSection === sectionId && sectionId !== 'fonts' && sectionId !== 'luafilters'}
                  on:dragover={(e) => handleTemplateSectionDragOver(e, sectionId)}
                  on:dragleave={handleTemplateSectionDragLeave}
                  on:drop={(e) => handleTemplateSectionDrop(e, sectionId)}
                >
                  <button
                    class="template-section-header"
                    class:collapsed={collapsedTemplateSections[sectionId]}
                    on:click={() => toggleTemplateSection(sectionId)}
                  >
                    <span class="section-toggle">
                      {collapsedTemplateSections[sectionId] ? '▶' : '▼'}
                    </span>
                    <span class="section-icon">{section.icon}</span>
                    <span class="section-title">{section.label}</span>
                    <span class="section-count">{sectionTemplates.length}</span>
                  </button>

                  {#if !collapsedTemplateSections[sectionId]}
                    <div class="template-section-content">
                      {#if sectionTemplates.length === 0}
                        <div class="section-empty">
                          <span class="section-empty-text">{section.description}</span>
                        </div>
                      {:else}
                        {#each sectionTemplates as template (template.id)}
                          <div
                            class="template-item"
                            class:selected={selectedTemplate?.id === template.id}
                            class:draggable={template.templateType === 'css'}
                            draggable={template.templateType === 'css'}
                            on:click={() => handleSelectTemplate(template)}
                            on:dragstart={(e) => handleTemplateDragStart(e, template)}
                            on:dragend={handleTemplateDragEnd}
                          >
                            <div class="template-icon">
                              {getTemplateIcon(template.templateType)}
                            </div>
                            <div class="template-info">
                              <div class="template-filename" title={template.filename}>
                                {template.filename}
                              </div>
                              <div class="template-meta">
                                <span class="template-type">{template.templateType.toUpperCase()}</span>
                                {#if presetTemplateIds.has(template.id)}
                                  <span class="separator">·</span>
                                  <span class="preset-tag">PRESET</span>
                                {/if}
                                {#if template.templateType === 'preparo' && template.documentType}
                                  <span class="separator">·</span>
                                  <span class="doc-type-badge">{template.documentType}</span>
                                {:else}
                                  <span class="separator">·</span>
                                  <span>{formatFileSize(template.size)}</span>
                                {/if}
                              </div>
                            </div>
                            <div class="template-actions">
                              <button
                                class="btn-icon danger"
                                on:click|stopPropagation={() => handleDeleteTemplate(template)}
                                title={$i18n('templates.delete')}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        {/each}
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}

              <!-- 4. Style (CSS sub-group containing undefined, epub3, epub2) -->
              <div class="template-style-group">
                <button
                  class="template-section-header template-style-header"
                  class:collapsed={styleGroupCollapsed}
                  on:click={() => styleGroupCollapsed = !styleGroupCollapsed}
                >
                  <span class="section-toggle">
                    {styleGroupCollapsed ? '▶' : '▼'}
                  </span>
                  <span class="section-icon">🎨</span>
                  <span class="section-title">Style</span>
                  <span class="section-count">{styleTemplateCount}</span>
                </button>

                {#if !styleGroupCollapsed}
                  <div class="template-style-content">
                    {#each styleSectionOrder as sectionId}
                      {@const sectionTemplates = groupedTemplates[sectionId]}
                      {@const section = templateSections[sectionId]}
                      {#if sectionId === 'undefined' ? sectionTemplates.length > 0 : true}
                        <div
                          class="template-section-group template-style-subsection"
                          class:section-undefined={sectionId === 'undefined'}
                          class:drag-over={dragOverTemplateSection === sectionId}
                          on:dragover={(e) => handleTemplateSectionDragOver(e, sectionId)}
                          on:dragleave={handleTemplateSectionDragLeave}
                          on:drop={(e) => handleTemplateSectionDrop(e, sectionId)}
                        >
                          <button
                            class="template-section-header template-subsection-header"
                            class:collapsed={collapsedTemplateSections[sectionId]}
                            on:click={() => toggleTemplateSection(sectionId)}
                          >
                            <span class="section-toggle">
                              {collapsedTemplateSections[sectionId] ? '▶' : '▼'}
                            </span>
                            <span class="section-icon">{section.icon}</span>
                            <span class="section-title">{section.label}</span>
                            <span class="section-count">{sectionTemplates.length}</span>
                          </button>

                          {#if !collapsedTemplateSections[sectionId]}
                            <div class="template-section-content">
                              {#if sectionTemplates.length === 0}
                                <div class="section-empty">
                                  <span class="section-empty-text">{section.description}</span>
                                </div>
                              {:else}
                                {#each sectionTemplates as template (template.id)}
                                  <div
                                    class="template-item"
                                    class:selected={selectedTemplate?.id === template.id}
                                    class:draggable={template.templateType === 'css'}
                                    draggable={template.templateType === 'css'}
                                    on:click={() => handleSelectTemplate(template)}
                                    on:dragstart={(e) => handleTemplateDragStart(e, template)}
                                    on:dragend={handleTemplateDragEnd}
                                  >
                                    <div class="template-icon">
                                      {getTemplateIcon(template.templateType)}
                                    </div>
                                    <div class="template-info">
                                      <div class="template-filename" title={template.filename}>
                                        {template.filename}
                                      </div>
                                      <div class="template-meta">
                                        <span class="template-type">{template.templateType.toUpperCase()}</span>
                                        {#if presetTemplateIds.has(template.id)}
                                          <span class="separator">·</span>
                                          <span class="preset-tag">PRESET</span>
                                        {/if}
                                        <span class="separator">·</span>
                                        <span>{formatFileSize(template.size)}</span>
                                      </div>
                                    </div>
                                    <div class="template-actions">
                                      {#if !presetTemplateIds.has(template.id)}
                                      <button
                                        class="btn-icon danger"
                                        on:click|stopPropagation={() => handleDeleteTemplate(template)}
                                        title={$i18n('templates.delete')}
                                      >
                                        🗑️
                                      </button>
                                      {/if}
                                    </div>
                                  </div>
                                {/each}
                              {/if}
                            </div>
                          {/if}
                        </div>
                      {/if}
                    {/each}
                  </div>
                {/if}
              </div>

            </div>
          {/if}
        </div>

        <!-- Bibliography Style (CSL) - Independent section outside EPUB group -->
        <div class="template-csl-group">
          <button
            class="template-section-header template-csl-header"
            class:collapsed={cslGroupCollapsed}
            on:click={() => cslGroupCollapsed = !cslGroupCollapsed}
          >
            <span class="section-toggle">
              {cslGroupCollapsed ? '▶' : '▼'}
            </span>
            <span class="section-icon">📚</span>
            <span class="section-title">{$i18n('templates.cslTitle')}</span>
            <span class="section-count">{groupedTemplates.csl?.length || 0}</span>
          </button>

          {#if !cslGroupCollapsed}
            <div class="template-csl-content">
              {#if groupedTemplates.csl?.length === 0}
                <div class="section-empty">
                  <span class="section-empty-text">{$i18n('templates.cslEmpty')}</span>
                  <span class="section-empty-hint">{$i18n('templates.cslEmptyHint')}</span>
                </div>
              {:else}
                {#each groupedTemplates.csl as template (template.id)}
                  <div
                    class="template-item"
                    class:selected={selectedTemplate?.id === template.id}
                    on:click={() => handleSelectTemplate(template)}
                  >
                    <div class="template-icon">📚</div>
                    <div class="template-info">
                      <div class="template-filename" title={template.filename}>
                        {template.filename}
                      </div>
                      <div class="template-meta">
                        <span class="template-type">CSL</span>
                        <span class="separator">·</span>
                        <span>{formatFileSize(template.size)}</span>
                      </div>
                    </div>
                    <div class="template-actions">
                      <button
                        class="btn-icon danger"
                        on:click|stopPropagation={() => handleDeleteTemplate(template)}
                        title={$i18n('templates.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </div>

        <!-- InDesign group -->
        <div class="template-indesign-group">
          <button
            class="template-section-header template-indesign-header"
            class:collapsed={indesignGroupCollapsed}
            on:click={() => indesignGroupCollapsed = !indesignGroupCollapsed}
          >
            <span class="section-toggle">
              {indesignGroupCollapsed ? '▶' : '▼'}
            </span>
            <span class="section-icon">📐</span>
            <span class="section-title">InDesign</span>
            <span class="section-count">{indesignTemplateCount}</span>
          </button>

          {#if !indesignGroupCollapsed}
            <div class="template-indesign-content">
              {#if groupedTemplates.indesign?.length === 0}
                <div class="section-empty">
                  <span class="section-empty-text">InDesign template files (.indd)</span>
                  <span class="section-empty-hint">Import .indd templates (titlepage, bodymatter, backmatter, endpage...)</span>
                </div>
              {:else}
                {#each groupedTemplates.indesign as template (template.id)}
                  <div
                    class="template-item"
                    class:selected={selectedTemplate?.id === template.id}
                    on:click={() => handleSelectTemplate(template)}
                  >
                    <div class="template-icon">📐</div>
                    <div class="template-info">
                      <div class="template-filename" title={template.filename}>
                        {template.filename}
                      </div>
                      <div class="template-meta">
                        <span class="template-type">INDESIGN</span>
                        <span class="separator">·</span>
                        <span>{formatFileSize(template.size)}</span>
                      </div>
                    </div>
                    <div class="template-actions">
                      <button
                        class="btn-icon danger"
                        on:click|stopPropagation={() => handleDeleteTemplate(template)}
                        title={$i18n('templates.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
  {/if}

  <!-- Config Panel (shown when config button is clicked) -->
  {#if showConfigPanel}
  <div class="config-overlay">
    <ProjectConfigPanel on:close={() => { showConfigPanel = false; if ($presetMode) { presetMode.set(false); selectedPreset.set(null); } }} />
  </div>
  {/if}

  <!-- Menu contextuel -->
  {#if contextMenuDoc}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      bind:this={contextMenuElement}
      class="context-menu"
      style="left: {contextMenuPosition.x}px; top: {contextMenuPosition.y}px;"
      on:click|stopPropagation
    >
      {#if selectedDocs.size > 1}
        <div class="context-menu-header">
          {selectedDocs.size} documents sélectionnés
        </div>
        <div class="context-menu-divider"></div>
      {/if}
      <div class="context-menu-section">
        <button
          class="context-menu-item"
          class:disabled={!canMoveUp(contextMenuDoc) || selectedDocs.size > 1}
          on:click={handleMoveUp}
          disabled={!canMoveUp(contextMenuDoc) || selectedDocs.size > 1}
        >
          ↑ {$i18n('docs.moveUp')}
        </button>
        <button
          class="context-menu-item"
          class:disabled={!canMoveDown(contextMenuDoc) || selectedDocs.size > 1}
          on:click={handleMoveDown}
          disabled={!canMoveDown(contextMenuDoc) || selectedDocs.size > 1}
        >
          ↓ {$i18n('docs.moveDown')}
        </button>
      </div>
      <div class="context-menu-divider"></div>
      <!-- Document Type Submenu -->
      <div class="context-menu-submenu-wrapper">
        <button
          class="context-menu-item has-submenu"
          class:active={showTypeSubmenu}
          on:click={toggleTypeSubmenu}
        >
          📋 Définir le type
          <span class="submenu-arrow">{showTypeSubmenu ? '◀' : '▶'}</span>
        </button>
        {#if showTypeSubmenu}
          <div class="context-submenu">
            <button
              class="context-menu-item"
              on:click={() => handleSetDocumentType(null)}
            >
              ❌ Aucun type
            </button>
            <div class="context-menu-divider"></div>
            {#each Object.entries(DOCUMENT_TYPES) as [category, categoryData]}
              <div class="submenu-category">{categoryData.label}</div>
              {#each categoryData.types as typeOption (typeOption.value)}
                {@const docType = typeOption.value}
                <button
                  class="context-menu-item submenu-item"
                  class:active={selectedDocs.size === 1 && contextMenuDoc?.documentType === docType}
                  on:click={() => handleSetDocumentType(docType)}
                >
                  {typeOption.label}
                </button>
              {/each}
            {/each}
          </div>
        {/if}
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-section">
        <button
          class="context-menu-item"
          class:active={contextMenuDoc.status === 'todo'}
          on:click={() => handleChangeStatus('todo')}
        >
          {statusIcons['todo']} {$i18n('docs.statusTodo')}
        </button>
        <button
          class="context-menu-item"
          class:active={contextMenuDoc.status === 'in-progress'}
          on:click={() => handleChangeStatus('in-progress')}
        >
          {statusIcons['in-progress']} {$i18n('docs.statusInProgress')}
        </button>
        <button
          class="context-menu-item"
          class:active={contextMenuDoc.status === 'done'}
          on:click={() => handleChangeStatus('done')}
        >
          {statusIcons['done']} {$i18n('docs.statusDone')}
        </button>
      </div>
      <div class="context-menu-divider"></div>
      <button class="context-menu-item" on:click={handleEditDoc} disabled={selectedDocs.size > 1}>
        ✏️ {$i18n('docs.edit')}
      </button>
      <button class="context-menu-item danger" on:click={handleDeleteDoc}>
        🗑️ {selectedDocs.size > 1 ? $i18n('docs.deleteMultiple', { count: selectedDocs.size }) : $i18n('docs.delete')}
      </button>
    </div>
  {/if}

  <!-- Modal d'import de document -->
  {#if showImporter}
    <div class="modal-overlay" on:click={() => showImporter = false}>
      <div class="modal-content" on:click|stopPropagation>
        <button
          class="modal-close"
          on:click={() => showImporter = false}
          title="Close"
        >
          ✕
        </button>
        <DocumentImporter
          on:converted={handleDocumentConverted}
          on:imagesImported={handleImagesImported}
          on:templatesImported={handleTemplatesImported}
          on:metadataImported={handleMetadataImported}
        />
      </div>
    </div>
  {/if}

  <!-- Toast notification -->
  {#if toastVisible}
    <div class="toast">{toastMessage}</div>
  {/if}

</div>

<style>
  .docs-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-background);
    position: relative;
  }

  /* Header */
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
  }

  .header-left {
    flex: 1;
    min-width: 0;
  }

  .panel-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .project-badge {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.25rem 0.5rem;
    background: var(--accent-color-light);
    color: var(--accent-color);
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
  }

  .btn-icon {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.4rem 0.6rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
  }

  .btn-icon:hover:not(:disabled) {
    background: var(--hover-background);
    border-color: var(--accent-color);
  }

  .btn-icon:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Toolbar */
  .toolbar {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    gap: 0.75rem;
  }

  .search-bar {
    flex: 1;
  }

  .search-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    background: var(--panel-background);
    color: var(--text-primary);
  }

  .search-input:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .filter-select {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    background: var(--panel-background);
    color: var(--text-primary);
    cursor: pointer;
  }

  .filter-select:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  /* Formulaire nouveau document */
  .new-doc-form,
  .new-template-form {
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--hover-background);
  }

  .document-type-selector {
    width: 100%;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: inherit;
    background: var(--panel-background);
    color: var(--text-primary);
    cursor: pointer;
  }

  .document-type-selector:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .doc-type-badge {
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
    background: var(--accent-color-light);
    color: var(--accent-color);
    border-radius: 4px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .form-input,
  .form-textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: inherit;
    background: var(--panel-background);
    color: var(--text-primary);
  }

  .form-textarea {
    resize: vertical;
  }

  .form-input:focus,
  .form-textarea:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .form-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: inherit;
    background: var(--panel-background);
    color: var(--text-primary);
    cursor: pointer;
  }

  .form-actions {
    display: flex;
    gap: 0.5rem;
  }

  .form-hint {
    margin: 0.5rem 0 0 0;
    font-size: 0.75rem;
    color: var(--text-muted);
    font-style: italic;
  }

  .btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary {
    background: var(--accent-color);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }

  .btn-secondary:hover {
    background: var(--hover-background);
  }

  /* En-têtes de colonnes */
  .table-header {
    display: flex;
    padding: 0.75rem 1rem;
    background: var(--hover-background);
    border-bottom: 1px solid var(--border-color);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
  }

  .header-cell {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    transition: color 0.15s;
  }

  .header-cell:hover {
    color: var(--accent-color);
  }

  .col-status {
    width: 80px;
    flex-shrink: 0;
  }

  .col-title {
    flex: 1;
    min-width: 0;
  }

  .col-date {
    width: 100px;
    flex-shrink: 0;
  }

  .sort-indicator {
    font-size: 0.9rem;
  }

  /* Liste des documents */
  .docs-list {
    flex: 1;
    overflow-y: auto;
  }

  /* Sections (frontmatter, bodymatter, backmatter) */
  .section-group {
    border-bottom: 1px solid var(--border-color);
  }

  /* Section pour les documents sans type défini */
  .section-group.section-undefined {
    background: rgba(245, 158, 11, 0.05);
    border-left: 3px solid var(--warning-color, #f59e0b);
  }

  .section-group.section-undefined .section-header {
    background: rgba(245, 158, 11, 0.1);
  }

  .section-group.section-undefined .section-count {
    background: rgba(245, 158, 11, 0.2);
    color: var(--warning-color, #f59e0b);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.6rem 1rem;
    background: var(--hover-background);
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
  }

  .section-header:hover {
    background: var(--code-background);
  }

  .section-header.collapsed {
    opacity: 0.7;
  }

  .section-toggle {
    font-size: 0.7rem;
    color: var(--text-muted);
    width: 1rem;
  }

  .section-icon {
    font-size: 1rem;
  }

  .section-title {
    flex: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-count {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.15rem 0.5rem;
    background: var(--accent-color-light);
    color: var(--accent-color);
    border-radius: 10px;
  }

  .section-docs {
    background: var(--panel-background);
  }

  .section-empty {
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    font-size: 0.8rem;
    color: var(--text-muted);
    font-style: italic;
  }

  .doc-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);
    transition: background 0.15s;
    user-select: none;
    -webkit-user-select: none;
  }

  .doc-item:hover {
    background: var(--hover-background);
  }

  .doc-item.selected {
    background: var(--accent-color-light);
    border-left: 3px solid var(--accent-color);
  }

  .doc-item.multi-selected {
    background: rgba(var(--accent-color-rgb, 59, 130, 246), 0.15);
    border-left: 3px solid var(--accent-color);
  }

  .doc-item.multi-selected.selected {
    background: var(--accent-color-light);
  }

  /* Drag & Drop styles */
  .doc-item[draggable="true"] {
    cursor: grab;
  }

  .doc-item[draggable="true"]:active {
    cursor: grabbing;
  }

  .doc-item.drag-over {
    border-top: 2px solid var(--accent-color);
    background: var(--accent-color-light);
  }

  .doc-item:global(.dragging) {
    opacity: 0.5;
    background: var(--hover-background);
  }

  .doc-order {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-muted);
    background: var(--hover-background);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    min-width: 1.5rem;
    text-align: center;
    flex-shrink: 0;
  }

  .doc-status {
    font-size: 1.2rem;
    width: 40px;
    flex-shrink: 0;
  }

  .doc-info {
    flex: 1;
    min-width: 0;
  }

  .doc-title {
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .doc-stats {
    font-size: 0.75rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    gap: 0.35rem;
    align-items: center;
  }

  .doc-stats .separator {
    opacity: 0.5;
  }

  .doc-filename {
    font-family: var(--font-family-mono);
    font-size: 0.7rem;
    color: var(--accent-color);
    background: var(--accent-color-light);
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
  }

  .doc-meta {
    width: 100px;
    flex-shrink: 0;
    text-align: right;
  }

  .doc-date {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  /* États */
  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    gap: 1rem;
    text-align: center;
    color: var(--text-muted);
  }

  .empty-icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  .spinner {
    width: 30px;
    height: 30px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-state {
    color: var(--error-color);
  }

  /* Menu contextuel */
  .context-menu {
    position: fixed;
    background: var(--panel-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 180px;
    padding: 0.5rem 0;
  }

  .context-menu-section {
    padding: 0.25rem 0;
  }

  .context-menu-label {
    padding: 0.4rem 1rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
  }

  .context-menu-item {
    display: block;
    width: 100%;
    padding: 0.6rem 1rem;
    border: none;
    background: transparent;
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s;
  }

  .context-menu-item:hover {
    background: var(--hover-background);
  }

  .context-menu-item.active {
    background: var(--accent-color-light);
    color: var(--accent-color);
  }

  .context-menu-item.danger {
    color: var(--error-color);
  }

  .context-menu-item.danger:hover {
    background: rgba(239, 68, 68, 0.1);
  }

  .context-menu-item.disabled,
  .context-menu-item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .context-menu-item.disabled:hover,
  .context-menu-item:disabled:hover {
    background: transparent;
  }

  .context-menu-divider {
    height: 1px;
    background: var(--border-color);
    margin: 0.5rem 0;
  }

  .context-menu-header {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--accent-color);
    background: var(--accent-color-light);
  }

  /* Submenu styles */
  .context-menu-submenu-wrapper {
    position: relative;
  }

  .context-menu-item.has-submenu {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .submenu-arrow {
    font-size: 0.7rem;
    margin-left: 0.5rem;
  }

  .context-submenu {
    position: absolute;
    left: 100%;
    top: 0;
    background: var(--panel-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 180px;
    max-height: 400px;
    overflow-y: auto;
    padding: 0.5rem 0;
    z-index: 1001;
  }

  .submenu-category {
    padding: 0.4rem 1rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    background: var(--hover-background);
  }

  .context-menu-item.submenu-item {
    padding-left: 1.5rem;
    font-size: 0.8rem;
  }

  /* Header actions */
  .header-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .btn-import,
  .btn-export {
    font-size: 1.2rem;
  }

  .btn-import:hover:not(:disabled),
  .btn-export:hover:not(:disabled) {
    background: var(--accent-color-light);
  }

  /* Export dropdown */
  .export-dropdown {
    position: relative;
  }

  .export-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    background: var(--panel-background, white);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    min-width: 200px;
    overflow: hidden;
  }

  .export-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
  }

  .export-option:hover {
    background: var(--hover-background, #f3f4f6);
  }

  .export-option:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }

  .export-separator {
    margin: 0.25rem 0;
    border: none;
    border-top: 1px solid var(--border-color);
  }

  /* Modal d'import */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal-content {
    position: relative;
    background: var(--panel-background);
    border-radius: 8px;
    padding: 2rem;
    max-width: 700px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--text-muted);
    padding: 0.25rem;
    line-height: 1;
    border-radius: 4px;
    transition: all 0.2s;
    z-index: 1;
  }

  .modal-close:hover {
    background: var(--hover-background);
    color: var(--error-color);
    transform: scale(1.1);
  }

  /* Tabs */
  .panel-tabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--border-color);
    padding: 0 1rem;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-muted);
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .tab-button:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .tab-button.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  .tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.25rem;
    padding: 0 0.4rem;
    background: var(--bg-tertiary);
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .tab-button.active .tab-count {
    background: var(--accent-color-light);
    color: var(--accent-color);
  }

  /* Images Tab */
  .images-tab {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .images-list {
    flex: 1;
    overflow-y: auto;
  }

  /* Image Sections */
  .image-section-group {
    border-bottom: 1px solid var(--border-color);
    transition: all 0.2s;
  }

  .image-section-group.section-cover {
    background: rgba(16, 185, 129, 0.05);
    border-left: 3px solid var(--success-color, #10b981);
  }

  .image-section-group.section-cover .image-section-header {
    background: rgba(16, 185, 129, 0.1);
  }

  .image-section-group.section-cover .section-count {
    background: rgba(16, 185, 129, 0.2);
    color: var(--success-color, #10b981);
  }

  .image-section-group.drag-over {
    background: var(--accent-color-light);
    border-left: 3px solid var(--accent-color);
  }

  .image-section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.6rem 1rem;
    background: var(--hover-background);
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
  }

  .image-section-header:hover {
    background: var(--code-background);
  }

  .image-section-header.collapsed {
    opacity: 0.7;
  }

  .image-section-content {
    background: var(--panel-background);
  }

  .images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
    padding: 1rem;
  }

  .image-card {
    display: flex;
    flex-direction: column;
    background: var(--card-background);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    overflow: hidden;
    cursor: grab;
    transition: all 0.2s;
  }

  .image-card:active {
    cursor: grabbing;
  }

  .image-card:hover {
    border-color: var(--accent-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .image-card.selected {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 2px var(--accent-color-light);
  }

  .image-card:global(.dragging) {
    opacity: 0.5;
    background: var(--hover-background);
  }

  .image-preview {
    aspect-ratio: 1;
    background: var(--bg-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-info {
    padding: 0.5rem;
  }

  .image-filename {
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0.25rem;
  }

  .image-meta {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.65rem;
    color: var(--text-muted);
  }

  .image-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem 0.5rem;
    border-top: 1px solid var(--border-color);
    background: var(--bg-secondary);
  }

  .image-actions .btn-icon {
    padding: 0.25rem;
    font-size: 0.875rem;
  }

  .image-actions .btn-icon.danger:hover {
    color: var(--error-color);
  }

  /* Config Panel Overlay */
  .config-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--panel-background);
    z-index: 100;
    display: flex;
    flex-direction: column;
  }

  .btn-config {
    font-size: 1.1rem;
  }

  .btn-config:hover:not(:disabled) {
    background: var(--accent-color-light);
  }

  .tab-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Templates Tab */
  .templates-tab {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .templates-list {
    flex: 1;
    overflow-y: auto;
  }

  /* ePub Parent Group */
  .template-epub-group {
    border-bottom: 1px solid var(--border-color);
  }

  .template-epub-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.7rem 1rem;
    background: var(--hover-background);
    border: none;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
    border-bottom: 1px solid var(--border-color);
  }

  .template-epub-header:hover {
    background: var(--code-background);
  }

  .template-epub-header.collapsed {
    opacity: 0.7;
    border-bottom: none;
  }

  .template-epub-content {
    padding-left: 0.5rem;
  }

  /* Template Sections */
  .template-section-group {
    border-bottom: 1px solid var(--border-color);
    transition: all 0.2s;
  }

  .template-section-group.section-undefined {
    background: rgba(245, 158, 11, 0.05);
    border-left: 3px solid var(--warning-color, #f59e0b);
  }

  .template-section-group.section-undefined .template-section-header {
    background: rgba(245, 158, 11, 0.1);
  }

  .template-section-group.section-undefined .section-count {
    background: rgba(245, 158, 11, 0.2);
    color: var(--warning-color, #f59e0b);
  }

  .template-section-group.drag-over {
    background: var(--accent-color-light);
    border-left: 3px solid var(--accent-color);
  }

  .template-section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.6rem 1rem;
    background: var(--hover-background);
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
  }

  .template-section-header:hover {
    background: var(--code-background);
  }

  .template-section-header.collapsed {
    opacity: 0.7;
  }

  /* Style sub-group (CSS sections) */
  .template-style-group {
    border-bottom: 1px solid var(--border-color);
  }

  .template-style-header {
    font-weight: 600;
  }

  .template-style-content {
    padding-left: 0.5rem;
  }

  .template-style-subsection {
    border-bottom-color: var(--border-color);
  }

  .template-subsection-header {
    font-size: 0.75rem;
    padding: 0.5rem 1rem;
  }

  /* CSL group (Bibliography Style) */
  .template-csl-group {
    border-bottom: 1px solid var(--border-color);
  }

  .template-csl-header {
    font-weight: 600;
  }

  .template-csl-content {
    padding: 0.25rem 0.5rem;
    background: var(--panel-background);
  }

  .template-section-content {
    background: var(--panel-background);
    padding: 0.25rem 0.5rem;
  }

  .template-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    background: var(--card-background);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .template-item:hover {
    border-color: var(--accent-color);
    background: var(--hover-background);
  }

  .template-item.selected {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 2px var(--accent-color-light);
  }

  .template-item.draggable {
    cursor: grab;
  }

  .template-item.draggable:active {
    cursor: grabbing;
  }

  .template-item:global(.dragging) {
    opacity: 0.5;
    background: var(--hover-background);
  }

  .template-icon {
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border-radius: 8px;
  }

  .template-info {
    flex: 1;
    min-width: 0;
  }

  .template-filename {
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0.25rem;
  }

  .template-meta {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .template-type {
    font-family: var(--font-family-mono);
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
    background: var(--accent-color-light);
    color: var(--accent-color);
    border-radius: 4px;
    font-weight: 600;
  }

  .preset-tag {
    font-family: var(--font-family-mono);
    font-size: 0.6rem;
    padding: 0.1rem 0.35rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 3px;
    font-weight: 600;
  }

  .template-actions {
    display: flex;
    gap: 0.25rem;
  }

  .template-actions .btn-icon {
    padding: 0.4rem;
    font-size: 0.875rem;
  }

  .template-actions .btn-icon.danger:hover {
    color: var(--error-color);
    background: rgba(239, 68, 68, 0.1);
  }

  .empty-hint {
    font-size: 0.8rem;
    color: var(--text-muted);
    opacity: 0.7;
  }

  /* Toast notification */
  .toast {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.75rem 1.5rem;
    background: var(--text-primary);
    color: var(--panel-background);
    border-radius: 4px;
    font-size: 0.875rem;
    z-index: 1000;
    animation: toast-fade 2s ease-in-out;
  }

  @keyframes toast-fade {
    0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
    10% { opacity: 1; transform: translateX(-50%) translateY(0); }
    90% { opacity: 1; transform: translateX(-50%) translateY(0); }
    100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
  }

  /* NOTE: Les variables CSS sont définies globalement dans variables.css */
</style>
